import { VerificationsInterviewDetailsOption } from '__core/shared/model/tool/VerificationsInterviewDetails.enum';
import { VerificationsEmploymentOption } from '__core/shared/model/tool/VerificationsEmployment.enum';
import { isHomeLoanPreferredProductFixedInterestOnlyLineOfCreditOrInterestInAdvance } from '__core/shared/util/salestrekker';
import template from './editOwnershipOfAssetOrLiability.template.html';

/* @ngInject */

export default (
  $q,
  $rootScope,
  $timeout,
  $mdDialog,
  dateTimeService,
  Income,
  Asset,
  Liability,
  Ownership,
  Insurance,
  OtherAdviser,
  RiskInsuranceProduct,
  TicketLoanFeeType,
  TicketCommercialLoanSecuritySplitType,
  TicketSharedWithClientProfileType,
  ContactResidencyStatusOption,
  ContactEmploymentBasis,
  ContactEmploymentType,
  Address,
  AssetFinanceAssetAge,
  YesNoOption,
  CreditRecordOption,
  ImportantOption,
  LevelOption,
  FrequencyOption,
  PersonalTitle,
  OwnershipType,
  PropertyType,
  ObjectivePayBallonOption,
  PaymentFrequency,
  BankAccountType,
  ObjectivePaymentPreferenceOption,
  ObjectivePreferredDurationOption,
  ObjectivePaymentPlanOption,
  ContactResidentialStatusOption,
  ticketFactFindService,
  uiService,
  ADDRESS,
  ASSET_FINANCE,
  DOCUMENT,
  CONTACT,
  GLOBAL,
  TICKET,
  HOME_LOAN,
  PRODUCT,
  PDF,
  INSTANCE,
  FINANCE
) => {
  /**
   * Set other date format.
   *
   */
  const setOtherDateFormat = {
    formatDate(date) {
      return _.isDate(date) ? moment(date).format($rootScope.CurrentDateFormatSecondary()) : '';
    },
    parseDate(date) {
      const m = moment(date, $rootScope.CurrentDateFormatSecondary(), true);
      return m.isValid() ? m.toDate() : new Date(NaN);
    },
    isDateComplete(date) {
      const dateString = date.trim();
      // Look for two chunks of content (either numbers or text) separated by delimiters.
      const re = /^(([a-zA-Z]{3,}|[0-9]{1,4})([ .,]+|[/-]))([a-zA-Z]{3,}|[0-9]{1,4})/;
      return re.test(dateString);
    }
  };

  /**
   * Get organization country.
   *
   * @returns {String}
   */
  const getOrganizationCountry = () => {
    const name =
      $rootScope.CurrentOrganization.organization.location &&
      $rootScope.CurrentOrganization.organization.location.name &&
      $rootScope.CurrentOrganization.organization.location.name.common
        ? $rootScope.CurrentOrganization.organization.location.name.common
        : '';
    const cca2 =
      $rootScope.CurrentOrganization.organization.location &&
      $rootScope.CurrentOrganization.organization.location.cca2
        ? $rootScope.CurrentOrganization.organization.location.cca2
        : INSTANCE.cca2;
    return { name, cca2 };
  };

  /**
   * Get organization country.
   *
   * @returns {Object}
   */
  const getOrganizationVat = () => {
    const percent = parseFloat($rootScope?.CurrentOrganization?.organization?.vat || 0);
    const decimal = percent ? _.divide(percent, 100) : 0;
    return { percent, decimal };
  };

  /**
   * Get GST Threshold.
   *
   * @returns {Number}
   */
  const getGstThreshold = () => 6191.64;

  /**
   * Round the value
   *
   * @param {Number} value - Number to be rounded.
   *
   * @returns {Number}
   */
  const round = ({ value }) => {
    if (!value) {
      return 0;
    }
    return Math.round(value * 1e2) / 1e2;
  };

  /**
   * Compare two arrays of objects
   *
   * @param {Array} array
   * @param {Array} order
   * @param {String} key
   *
   * @returns {number}
   */
  const compareTwoArraysOfObjects = (x, y) => _(x).xorWith(y, _.isEqual).isEmpty();

  /**
   * Sort array of objects based on another array
   *
   * @param {Array} array
   * @param {Array} order
   * @param {String} key
   *
   * @returns {number}
   */
  const sortArrayOfObjectsBasedOnAnotherArray = ({ array, order, key }) => {
    array.sort((a, b) => {
      const aarA = a[key];
      const arrB = b[key];
      if (order.indexOf(aarA) > order.indexOf(arrB)) {
        return 1;
      }
      return -1;
    });
    return array;
  };

  /**
   * Get ticket contact model
   *
   * @param {Array} array
   * @param {Array} order
   * @param {String} key
   *
   * @returns {number}
   */
  const getTicketContactModel = () => ({
    id: '',
    isCompany: false,
    name: '',
    familyName: '',
    phoneCode: '',
    phone: '',
    email: '',
    type: ''
  });

  /**
   * Get all client portal sections.
   *
   * @returns {Object}
   */
  const getAllClientPortalSections = () => TicketSharedWithClientProfileType.available();

  /**
   * Get available client portal sections.
   *
   * @param {Object} workflow
   *
   * @returns {Object}
   */
  const getAvailableClientPortalSections = ({ workflow }) => {
    if (!_.isEmpty(workflow) && !workflow.isAssetFinance() && !workflow?.isHomeLoan()) {
      return _.reject(
        getAllClientPortalSections(),
        (obj) =>
          obj?.name === TicketSharedWithClientProfileType.product_requirements.name ||
          obj?.name === TicketSharedWithClientProfileType.advisors.name
      );
    }
    if (!_.isEmpty(workflow) && workflow?.isAssetFinance()) {
      return _.reject(
        getAllClientPortalSections(),
        (obj) => obj?.name === TicketSharedWithClientProfileType.product_requirements.name
      );
    }
    if (!_.isEmpty(workflow) && workflow?.isHomeLoan()) {
      return _.reject(
        getAllClientPortalSections(),
        (obj) => obj?.name === TicketSharedWithClientProfileType.advisors.name
      );
    }
    return getAllClientPortalSections();
  };

  /**
   * Get available repayment indexes.
   *
   * @param {Object} workflow
   *
   * @returns {Object}
   */
  const getAvailableRepaymentIndexes = ({ workflow }) => {
    let creditCardRate = (getOrganizationCountry().cca2 === 'NZ' ? 3 : 3.8) / 100;
    let overdraftRate = (getOrganizationCountry().cca2 === 'NZ' ? 1.8 : 100) / 100;
    if (!_.isEmpty(workflow) && workflow?.isAssetFinance()) {
      creditCardRate =
        ($rootScope.CurrentOrganization?.financialPositionBuffers?.creditCardRate?.af ||
          (getOrganizationCountry().cca2 === 'NZ' ? 3 : 3.8)) / 100;
      overdraftRate =
        ($rootScope.CurrentOrganization?.financialPositionBuffers?.overdraftRate?.af ||
          (getOrganizationCountry().cca2 === 'NZ' ? 1.8 : 100)) / 100;
    }
    if (!_.isEmpty(workflow) && workflow?.isHomeLoan()) {
      creditCardRate =
        ($rootScope.CurrentOrganization?.financialPositionBuffers?.creditCardRate?.hl ||
          (getOrganizationCountry().cca2 === 'NZ' ? 3 : 3.8)) / 100;
      overdraftRate =
        ($rootScope.CurrentOrganization?.financialPositionBuffers?.overdraftRate?.hl ||
          (getOrganizationCountry().cca2 === 'NZ' ? 1.8 : 100)) / 100;
    }
    return { creditCardRate, overdraftRate };
  };

  /**
   * Get primary buffer.
   *
   * @param {Object} workflow
   * @param {String} bufferType
   *
   * @returns {Array}
   */
  const getPrimaryBuffer = ({ workflow, bufferType }) => {
    let buffer = 1;
    switch (bufferType) {
      case 'INCOME':
        if (!_.isEmpty(workflow) && workflow?.isAssetFinance()) {
          buffer =
            ($rootScope.CurrentOrganization?.financialPositionBuffers?.income
              ?.rentalIncomeShading?.af || (getOrganizationCountry().cca2 === 'NZ' ? 75 : 80)) /
            100;
        }
        if (!_.isEmpty(workflow) && workflow?.isHomeLoan()) {
          buffer =
            ($rootScope.CurrentOrganization?.financialPositionBuffers?.income
              ?.rentalIncomeShading?.hl || (getOrganizationCountry().cca2 === 'NZ' ? 75 : 80)) /
            100;
        }
        break;
      case 'EXISTING_REPAYMENT':
        if (!_.isEmpty(workflow) && workflow?.isAssetFinance()) {
          buffer =
            $rootScope.CurrentOrganization?.financialPositionBuffers?.existingRepayment
              ?.multiplier?.af || 1.35;
        }
        if (!_.isEmpty(workflow) && workflow?.isHomeLoan()) {
          buffer =
            $rootScope.CurrentOrganization?.financialPositionBuffers?.existingRepayment
              ?.multiplier?.hl || 1.35;
        }
        break;
      case 'PROPOSED_REPAYMENT':
        if (!_.isEmpty(workflow) && workflow?.isAssetFinance()) {
          buffer =
            $rootScope.CurrentOrganization?.financialPositionBuffers?.proposedRepayment
              ?.floorRate?.af || 0;
        }
        if (!_.isEmpty(workflow) && workflow?.isHomeLoan()) {
          buffer =
            ($rootScope.CurrentOrganization?.financialPositionBuffers?.proposedRepayment
              ?.floorRate?.hl || (getOrganizationCountry().cca2 === 'NZ' ? 7.5 : 7.25)) /
            100 /
            12;
        }
        break;
      default:
        break;
    }
    return buffer;
  };

  /**
   * Get primary buffer.
   *
   * @param {Object} workflow
   * @param {String} bufferType
   *
   * @returns {Array}
   */
  const getSecondaryBuffer = ({ workflow, bufferType }) => {
    let buffer = 1;
    switch (bufferType) {
      case 'INCOME':
        if (!_.isEmpty(workflow) && workflow?.isAssetFinance()) {
          buffer =
            ($rootScope.CurrentOrganization?.financialPositionBuffers?.income
              ?.totalIncomeShading?.af || (getOrganizationCountry().cca2 === 'NZ' ? 75 : 80)) /
            100;
        }
        if (!_.isEmpty(workflow) && workflow?.isHomeLoan()) {
          buffer =
            ($rootScope.CurrentOrganization?.financialPositionBuffers?.income
              ?.totalIncomeShading?.hl || (getOrganizationCountry().cca2 === 'NZ' ? 75 : 80)) /
            100;
        }
        break;
      case 'EXISTING_REPAYMENT':
        if (!_.isEmpty(workflow) && workflow?.isAssetFinance()) {
          buffer =
            $rootScope.CurrentOrganization?.financialPositionBuffers?.existingRepayment?.buffer
              ?.af || 0;
        }
        if (!_.isEmpty(workflow) && workflow?.isHomeLoan()) {
          buffer =
            $rootScope.CurrentOrganization?.financialPositionBuffers?.existingRepayment?.buffer
              ?.hl || 0;
        }
        break;
      case 'PROPOSED_REPAYMENT':
        if (!_.isEmpty(workflow) && workflow?.isAssetFinance()) {
          buffer =
            $rootScope.CurrentOrganization?.financialPositionBuffers?.proposedRepayment?.buffer
              ?.af || 0;
        }
        if (!_.isEmpty(workflow) && workflow?.isHomeLoan()) {
          buffer =
            $rootScope.CurrentOrganization?.financialPositionBuffers?.proposedRepayment?.buffer
              ?.hl || 0;
        }
        break;
      default:
        break;
    }
    return buffer;
  };

  /**
   * Is using compare with HEM.
   *
   * @param {Object} workflow
   *
   * @returns {Array}
   */
  const isUsingCompareWithHem = ({ workflow }) => {
    let useHem = false;
    if (!_.isEmpty(workflow) && workflow?.isAssetFinance()) {
      useHem =
        $rootScope.CurrentOrganization?.financialPositionBuffers?.compareWithHem?.af || false;
    }
    if (!_.isEmpty(workflow) && workflow?.isHomeLoan()) {
      useHem =
        $rootScope.CurrentOrganization?.financialPositionBuffers?.compareWithHem?.hl || false;
    }
    return useHem;
  };

  /**
   * Get/set available compliance types.
   *
   * @param {Object} workflow
   * @param {Boolean} isAssetFinanceCommercial
   *
   * @returns {Array}
   */
  const getSetAvailableComplianceTypes = ({ workflow, isAssetFinanceCommercial }) => {
    const availableComplianceTypes = _.sortBy(DOCUMENT.TYPES, 'value');
    if (workflow?.isHomeLoan()) {
      return availableComplianceTypes;
    }
    if (workflow?.isAssetFinance()) {
      for (let i = 0; i < availableComplianceTypes.length; i += 1) {
        if (isAssetFinanceCommercial) {
          if (availableComplianceTypes[i].id === 1 || availableComplianceTypes[i].id === 2) {
            availableComplianceTypes[i].isRequired = true;
            availableComplianceTypes[i].isRequiredForCompliance = true;
          } else {
            availableComplianceTypes[i].isRequired = false;
            availableComplianceTypes[i].isRequiredForCompliance = false;
          }
        } else if (!isAssetFinanceCommercial) {
          if (availableComplianceTypes[i].id === 1 || availableComplianceTypes[i].id === 2) {
            availableComplianceTypes[i].isRequired = true;
            availableComplianceTypes[i].isRequiredForCompliance = false;
          } else if (
            availableComplianceTypes[i].id === 4 ||
            availableComplianceTypes[i].id === 5
          ) {
            availableComplianceTypes[i].isRequired = true;
            availableComplianceTypes[i].isRequiredForCompliance = true;
          } else {
            availableComplianceTypes[i].isRequired = false;
            availableComplianceTypes[i].isRequiredForCompliance = false;
          }
        }
      }
      return availableComplianceTypes;
    }
    return availableComplianceTypes;
  };

  /**
   * Get available states.
   *
   * @returns {Array}
   */
  const getAvailableAddressStates = () => _.sortBy(ADDRESS.STATES, 'weight');

  /**
   * Get available street types.
   *
   * @returns {Array}
   */
  const getAvailableAddressStreetTypes = () => _.sortBy(ADDRESS.STREET_TYPES.AU, 'weight');

  /**
   * Get available contact employment basis.
   *
   * @param {Object} employmentType
   *
   * @returns {Array}
   */
  const getAvailableContactEmploymentBasis = ({ employmentType }) => {
    if (employmentType?.name === ContactEmploymentType.salaried_employee.name) {
      return _.without(
        ContactEmploymentBasis.available(),
        ContactEmploymentBasis.sole_trader,
        ContactEmploymentBasis.in_a_company,
        ContactEmploymentBasis.in_a_partnership
      );
    }
    if (employmentType?.name === ContactEmploymentType.self_employed.name) {
      return _.without(
        ContactEmploymentBasis.available(),
        ContactEmploymentBasis.full_time,
        ContactEmploymentBasis.part_time,
        ContactEmploymentBasis.casual
      );
    }
    return [];
  };

  /**
   * Get available term in years.
   *
   * @param {Boolean} isRegular
   *
   * @returns {Array}
   */
  const getAvailableTermInYears = ({ isRegular }) => {
    if (isRegular) {
      return _.reject(GLOBAL.TERM_IN_YEARS, ['title', 'Not essential']);
    }
    return _.reject(GLOBAL.TERM_IN_YEARS, ['title', 'Select one']);
  };

  /**
   * Get available term in years shown in months.
   *
   * @param {Boolean} isRegular
   *
   * @returns {Array}
   */
  const getAvailableTermInYearsShownInMonths = ({ isRegular }) => {
    if (isRegular) {
      return _.reject(GLOBAL.TERM_IN_YEARS_SHOWN_IN_MONTHS, ['title', 'Not essential']);
    }
    return _.reject(GLOBAL.TERM_IN_YEARS_SHOWN_IN_MONTHS, ['title', 'Select one']);
  };

  /**
   * Get available term in months.
   *
   * @param {Boolean} isRegular
   *
   * @returns {Array}
   */
  const getAvailableTermInMonths = ({ isRegular }) => {
    if (isRegular) {
      return _.reject(GLOBAL.TERM_IN_MONTHS, ['title', 'Not essential']);
    }
    return _.reject(GLOBAL.TERM_IN_MONTHS, ['title', 'Select one']);
  };

  /**
   * Get available term in months shown in years.
   *
   * @param {Boolean} isRegular
   *
   * @returns {Array}
   */
  const getAvailableTermInMonthsShownInYears = ({ isRegular }) => {
    if (isRegular) {
      return _.reject(GLOBAL.TERM_IN_MONTHS_SHOWN_IN_YEARS, ['title', 'Not essential']);
    }
    return _.reject(GLOBAL.TERM_IN_MONTHS_SHOWN_IN_YEARS, ['title', 'Select one']);
  };

  /**
   * Get title of selected term in months.
   *
   * @param {Number} term
   *
   * @returns {String}
   */
  const getTitleOfSelectedTermInMonths = ({ term }) => {
    const currentTerm = _.find(
      getAvailableTermInMonths({ isRegular: true }),
      (t) => t?.value === term
    );
    return currentTerm && currentTerm.value ? currentTerm.title : '-';
  };

  /**
   * Get available years.
   *
   * @param {Boolean} isTaxYear
   *
   * @returns {String}
   */
  const getAvailableYears = ({ isTaxYear }) => {
    const years = [];
    const currentYear = parseInt(moment.utc().year(), 10);
    let financialEndDate = new Date(`06/30/${currentYear}`);
    if (getOrganizationCountry().cca2 === 'NZ') {
      financialEndDate = new Date(`03/31/${currentYear}`);
    }
    const len = isTaxYear ? 10 : 80;
    let useCurrentYear = true;
    if (isTaxYear) {
      useCurrentYear = moment.utc(financialEndDate).diff(moment.utc(), 'days', true) < 0;
    }
    const dateStart = useCurrentYear ? moment.utc() : moment.utc().subtract(1, 'years');
    for (let y = 0; y < len; y += 1) {
      const year = parseInt(dateStart.year(), 10);
      years.push({ value: year, title: `${year}` });
      dateStart.subtract(1, 'years');
    }
    years.unshift({ value: 0, title: 'Select one' });
    return years;
  };

  /**
   * Get available income last tax years.
   *
   * @returns {Array}
   */
  const getAvailableIIncomeLastTaxYears = () => getAvailableYears({ isTaxYear: true });

  /**
   * Get available income previous tax years.
   *
   * @returns {Array}
   */
  const getAvailableIIncomePreviousTaxYears = () => {
    const availableYears = getAvailableYears({ isTaxYear: true });
    const lastYear = availableYears[availableYears.length - 1]?.value
      ? {
          value: _.subtract(availableYears[availableYears.length - 1].value, 1),
          title: `${_.subtract(availableYears[availableYears.length - 1].value, 1)}`
        }
      : undefined;
    if (lastYear) {
      availableYears.push(lastYear);
    }
    return availableYears.splice(1);
  };

  /**
   * Get available interest only reason.
   *
   * @returns {Array}
   */
  const getAvailableInterestOnlyReason = () =>
    _.sortBy(TICKET.FACT_FIND.ARCHIVE.INTEREST_ONLY_REASON, 'value');

  /**
   * Get available ratings.
   *
   * @returns {Array}
   */
  const getAvailableRatings = () => _.sortBy(TICKET.FACT_FIND.ARCHIVE.RATING, 'weight');

  /**
   * Get available level options.
   *
   * @returns {Array}
   */
  const getAvailableLevelOptions = () => _.without(LevelOption.available(), LevelOption.none);

  /**
   * Get available payment frequencies.
   *
   * @param {Object} workflow
   * @param {String} loanPurpose
   * @param {Boolean} isExpense
   *
   * @returns {Array}
   */
  const getAvailablePaymentFrequencies = ({ workflow, loanPurpose, isExpense }) => {
    if (workflow?.isAssetFinance()) {
      if (loanPurpose === 'commercial') {
        return _.without(
          PaymentFrequency.available(),
          PaymentFrequency.none,
          PaymentFrequency.weekly,
          PaymentFrequency.fortnightly,
          PaymentFrequency.semiannual
        );
      }
      if (loanPurpose === 'consumer') {
        return _.without(
          PaymentFrequency.available(),
          PaymentFrequency.none,
          PaymentFrequency.weekly,
          PaymentFrequency.quarterly,
          PaymentFrequency.semiannual,
          PaymentFrequency.annually
        );
      }
    } else if (workflow?.isHomeLoan()) {
      return _.without(
        PaymentFrequency.available(),
        PaymentFrequency.none,
        PaymentFrequency.quarterly,
        PaymentFrequency.semiannual,
        PaymentFrequency.annually
      );
    } else if (isExpense) {
      return _.without(PaymentFrequency.available(), PaymentFrequency.none);
    }
    return PaymentFrequency.available();
  };

  /**
   * Get net income.
   *
   * @param {String} cca2
   *
   * @returns {Array}
   */
  const getNetIncome = (cca2) => TICKET.FACT_FIND.NET_INCOME[cca2];

  /**
   * Get available payment preference options.
   *
   * @returns {Array}
   */
  const getAvailableObjectivePaymentPreferenceOptions = () =>
    _.without(
      ObjectivePaymentPreferenceOption.available(),
      ObjectivePaymentPreferenceOption.none
    );

  /**
   * Get available monthly repayments.
   *
   * @returns {Array}
   */
  const getAvailableMonthlyRepayments = () =>
    _.sortBy(ASSET_FINANCE.DETAILS.MONTHLY_REPAYMENTS, 'id');

  /**
   * Get available interest rates.
   *
   * @returns {Array}
   */
  const getAvailableInterestRates = () => _.sortBy(ASSET_FINANCE.DETAILS.INTEREST_RATES, 'id');

  /**
   * Get available credit records.
   *
   * @returns {Array}
   */
  const getAvailableCreditRecordOptions = () => CreditRecordOption.available();

  /**
   * Get simple select options.
   *
   * @returns {Array}
   */
  const getSimpleSelectOptions = (isShort) => {
    const options = _.sortBy(HOME_LOAN.DETAILS.SIMPLE_SELECT_OPTIONS, 'weight');
    if (isShort) {
      const shortOptions = _.reject(options, ['id', 3]);
      return shortOptions;
    }
    return options;
  };

  /**
   * Get available interview methods.
   *
   * @returns {Array}
   */
  const getAvailableInterviewMethods = () =>
    _.sortBy(HOME_LOAN.DETAILS.INTERVIEW_METHODS, 'weight');

  /**
   * Get available compliance verifications interview details methods.
   *
   * @returns {Array}
   */
  const getAvailableComplianceVerificationsInterviewDetailsMethods = () =>
    VerificationsInterviewDetailsOption.available();

  /**
   * Get available income verification completed.
   *
   * @returns {Array}
   */
  const getAvailableIncomeVerificationCompleted = () =>
    _.sortBy(ASSET_FINANCE.DETAILS.INCOME_VERIFICATION_COMPLETED, 'weight');

  /**
   * Get available refinance types.
   *
   * @returns {Array}
   */
  const getAvailableRefinanceTypes = () =>
    _.sortBy(HOME_LOAN.DETAILS.REFINANCE_TYPES, 'weight');

  /**
   * Get available other contact types for valuation.
   *
   * @returns {Array}
   */
  const getAvailableOtherContactTypesForValuation = () =>
    _.sortBy(HOME_LOAN.DETAILS.VALUATION_CONTACT_TYPES, 'weight');

  /**
   * Get available mortgage priorities.
   *
   * @returns {Array}
   */
  const getAvailableMortgagePriorities = () =>
    _.sortBy(HOME_LOAN.DETAILS.MORTGAGE_PRIORITY, 'weight');

  /**
   * Get available nature of service sought.
   *
   * @returns {Array}
   */
  const getAvailableNatureOfServiceSought = () =>
    _.sortBy(HOME_LOAN.DETAILS.NEEDS_ANALYSIS.NATURE_OF_SERVICE_SOUGHT, 'weight');

  /**
   * Get available location of assets or funds.
   *
   * @returns {Array}
   */
  const getAvailableLocationOfAssetsOrFunds = () =>
    _.sortBy(HOME_LOAN.DETAILS.NEEDS_ANALYSIS.LOCATION_OF_ASSETS_OR_FUNDS, 'weight');

  /**
   * Get available location of assets or funds.
   *
   * @returns {Array}
   */
  const getAvailableLeadSourcingMethod = () =>
    _.sortBy(HOME_LOAN.DETAILS.NEEDS_ANALYSIS.LEAD_SOURCING_METHOD, 'weight');

  /**
   * Get available property status.
   *
   * @returns {Array}
   */
  const getAvailablePropertyStatus = () =>
    _.sortBy(HOME_LOAN.DETAILS.PROPERTY_STATUS, 'weight');

  /**
   * Get available construction types.
   *
   * @returns {Array}
   */
  const getAvailableConstructionTypes = () =>
    _.sortBy(HOME_LOAN.DETAILS.CONSTRUCTION_TYPES, 'weight');

  /**
   * Get available construction types.
   *
   * @returns {Array}
   */
  const getAvailableConstructionPropertyTypes = () =>
    _.sortBy(HOME_LOAN.DETAILS.CONSTRUCTION_PROPERTY_TYPES, 'weight');

  /**
   * Get available employment verifacation.
   *
   * @returns {Array}
   */
  const getAvailableEmploymentVerificationMethods = () =>
    _.sortBy(HOME_LOAN.DETAILS.EMPLOYMENT_VERIFICATION_METHOD, 'weight');

  /**
   * Get available compliance verifications employment options.
   *
   * @returns {Array}
   */
  const getAvailableComplianceVerificationsEmploymentOptions = () =>
    _.without(VerificationsEmploymentOption.available(), VerificationsEmploymentOption.none);

  /**
   * Get available yes/no radio options.
   *
   * @param {Number} option
   *
   * @returns {Array}
   */
  const getAvailableYesNoOptions = ({ option }) => {
    const availableYesNoOptions = _.without(YesNoOption.available(), YesNoOption.none);
    switch (option) {
      case 1:
        return _.without(
          availableYesNoOptions,
          YesNoOption.yes_discharged,
          YesNoOption.not_important,
          YesNoOption.unsure
        );
      case 2:
        return _.without(availableYesNoOptions, YesNoOption.not_important, YesNoOption.unsure);
      case 3:
        return _.without(availableYesNoOptions, YesNoOption.yes_discharged, YesNoOption.unsure);
      case 4:
        return _.without(
          availableYesNoOptions,
          YesNoOption.not_important,
          YesNoOption.yes_discharged
        );
      default:
        return availableYesNoOptions;
    }
  };

  /**
   * Get available yes/no select options.
   *
   * @param {Number} option
   *
   * @returns {Array}
   */
  const getAvailableYesNoSelectOptions = ({ option }) => {
    switch (option) {
      case 1:
        return _.without(
          YesNoOption.available(),
          YesNoOption.yes_discharged,
          YesNoOption.not_important,
          YesNoOption.unsure
        );
      case 2:
        return _.without(
          YesNoOption.available(),
          YesNoOption.not_important,
          YesNoOption.unsure
        );
      case 3:
        return _.without(
          YesNoOption.available(),
          YesNoOption.yes_discharged,
          YesNoOption.unsure
        );
      case 4:
        return _.without(
          YesNoOption.available(),
          YesNoOption.not_important,
          YesNoOption.yes_discharged
        );
      default:
        return YesNoOption.available();
    }
  };

  /**
   * Get available preferred duration options.
   *
   * @param {Number} option
   *
   * @returns {Array}
   */
  const getAvailableObjectivePreferredDurationOptions = ({ option }) => {
    const availableObjectivePreferredDurationOptionOptions = _.without(
      ObjectivePreferredDurationOption.available(),
      ObjectivePreferredDurationOption.none
    );
    switch (option) {
      case 1:
        return _.without(
          availableObjectivePreferredDurationOptionOptions,
          ObjectivePreferredDurationOption.up_to_one_year,
          ObjectivePreferredDurationOption.one_to_three_years,
          ObjectivePreferredDurationOption.three_to_five_years,
          ObjectivePreferredDurationOption.five_years_plus
        );
      case 2:
        return _.without(
          availableObjectivePreferredDurationOptionOptions,
          ObjectivePreferredDurationOption.six_months,
          ObjectivePreferredDurationOption.twelve_months,
          ObjectivePreferredDurationOption.eighteen_months,
          ObjectivePreferredDurationOption.two_years,
          ObjectivePreferredDurationOption.two_years_plus
        );
      default:
        break;
    }
    return availableObjectivePreferredDurationOptionOptions;
  };

  /**
   * Get available pyment plan options.
   *
   * @returns {Array}
   */
  const getAvailableObjectivePaymentPlanOptions = () =>
    _.without(ObjectivePaymentPlanOption.available(), ObjectivePaymentPlanOption.none);

  /**
   * Get available important options.
   *
   * @param {Number} option
   *
   * @returns {Array}
   */
  const getAvailableImportantOptions = ({ option }) => {
    const availableImportantOptions = _.without(
      ImportantOption.available(),
      ImportantOption.none
    );
    switch (option) {
      case 1:
        return _.without(
          availableImportantOptions,
          ImportantOption.most_important,
          ImportantOption.somewhat_important,
          ImportantOption.least_important
        );
      case 2:
        return _.without(
          availableImportantOptions,
          ImportantOption.important,
          ImportantOption.not_important,
          ImportantOption.do_not_want
        );
      default:
        break;
    }
    return availableImportantOptions;
  };

  /**
   * Get available how important options.
   *
   * @returns {Array}
   */
  const getAvailableHowImportantOptions = () =>
    _.sortBy(TICKET.FACT_FIND.ARCHIVE.HOW_IMPORTANT_OPTIONS, 'weight');

  /**
   * Get available frequency options.
   *
   * @returns {Array}
   */
  const getAvailableFrequencyOptions = () =>
    _.without(FrequencyOption.available(), FrequencyOption.none);

  /**
   * Get product fee type relations.
   *
   * @returns {Array}
   */
  const getProductFeeTypeRelations = () => HOME_LOAN.DETAILS.PRODUCT_FEE_TYPE_RELATIONS;

  /**
   * Get available product fee types.
   *
   * @returns {Array}
   */
  const getAvailableProductFeeTypes = () => PRODUCT.DETAILS.FEE_TYPE;

  /**
   * Get available asset ages.
   *
   * @param {Boolean} showAll
   *
   * @returns {Array}
   */
  const getAvailableAssetFinanceAssetAges = ({ showAll }) => {
    if (!showAll) {
      return _.without(AssetFinanceAssetAge.available(), AssetFinanceAssetAge.unsure);
    }
    return AssetFinanceAssetAge.available();
  };

  /**
   * Get available conveyancing fee types.
   *
   * @returns {Array}
   */
  const getAvailableConveyancingFeeTypes = () =>
    _.filter(TicketLoanFeeType.available(), (f) => f?.name !== 'commissions');

  /**
   * Get available real estate fee types.
   *
   * @returns {Array}
   */
  const getAvailableRealEstateFeeTypes = () =>
    _.filter(
      TicketLoanFeeType.available(),
      (f) => f?.name === 'disbursements' || f?.name === 'commissions'
    );

  /**
   * Get available PDF types.
   *
   * @returns {Array}
   */
  const getAvailablePdfTypes = () => PDF.PDF_TYPES;

  /**
   * Get available compliance types.
   *
   * @returns {Array}
   */
  const getAvailableComplianceTypes = () => _.sortBy(DOCUMENT.TYPES, 'value');

  /**
   * Get available client types for create documents option.
   *
   * @returns {Array}
   */
  const getAvailableClientTypesForCreateDocumentsOption = () =>
    _.sortBy(DOCUMENT.CLIENT_TYPES, 'weight');

  /**
   * Get Best interest duty flows
   *
   * @param {Object} workflow
   *
   * @returns {Array}
   */
  const getBidFlows = ({ workflow }) => {
    if (workflow.isAssetFinance()) {
      return _.reject(FINANCE.BID_FLOWS, ['id', 2]);
    }
    return FINANCE.BID_FLOWS;
  };

  /**
   * Get Date.
   *
   * @returns {Array}
   */
  const getDate = ({ timeStamp, dateFormat }) =>
    moment
      .utc(timeStamp || 0, 'X')
      .tz($rootScope.CurrentTimeZone() || 'UTC')
      .format(dateFormat || $rootScope.CurrentDateFormatPrimary() || 'LL');

  /**
   * Get correct number.
   *
   * @param {Number} value
   * @param {Boolean} isFloat
   *
   * @returns {Number}
   */
  const isNumber = (value, isFloat) => {
    if (isFloat) {
      return Number.isNaN(parseFloat(value)) ? 0 : parseFloat(value);
    }
    return Number.isNaN(parseInt(value, 10)) ? 0 : parseInt(value, 10);
  };

  /**
   * Get index by ID.
   *
   * @param {Array} array
   * @param {Number/String} id
   *
   * @returns {Number}
   */
  const findIndexById = (array, id) => _.findIndex(array || [], (item) => item?.id === id);

  /**
   * Get index by ID of Contact.
   *
   * @param {Array} array
   * @param {Number/String} idContact
   *
   * @returns {Number}
   */
  const findIndexByIdContact = ({ array, idContact }) =>
    _.findIndex(array || [], (item) => item?.idContact === idContact);

  /**
   * Get by idContact.
   *
   * @param {Array} array
   * @param {Number/String} id
   *
   * @returns {Number}
   */
  const findByIdContact = ({ array, idContact }) =>
    _.find(array || [], (item) => item?.idContact === idContact);

  /**
   * Rearrange array by index.
   *
   * @param {Array} array
   * @param {Number} oIdx
   * @param {Number} nIdx
   *
   * @returns {Array}
   */
  const rearrangeArrayByIndex = ({ array, oIdx, nIdx }) => {
    let oldIdx = oIdx;
    let newIdx = nIdx;
    while (oldIdx < 0) {
      oldIdx += array.length;
    }
    while (newIdx < 0) {
      newIdx += array.length;
    }
    if (newIdx >= array.length) {
      let k = newIdx - array.length;
      // eslint-disable-next-line no-plusplus
      while (k-- + 1) {
        array.push(undefined);
      }
    }
    array.splice(newIdx, 0, array.splice(oldIdx, 1)[0]);
    return array;
  };

  /**
   * Get ID labels of client.
   *
   * @returns {Array}
   */
  const getIdLabelsOfClient = () => [
    '018a40bf-027a-4a08-9910-a0cbb058ddab', // Client
    'ef8154b0-7bda-4d1e-a87e-f286c06f7d94', // Non-borrowing spouse
    'd583957c-3ee9-4ec2-b041-efa0f8d41875', // Security guarantor
    'e542c9e7-ba5c-4ac3-9234-52263b157271' // Servicing guarantor
  ];

  /**
   * Sort client labels.
   *
   * @param {Array} labels
   *
   * @returns {Array}
   */
  const sortContactLabels = ({ labels }) => {
    const len = labels?.length;
    for (let i = 0; i < len; i += 1) {
      const label = labels[i];
      if (label?.id === '018a40bf-027a-4a08-9910-a0cbb058ddab') {
        rearrangeArrayByIndex({
          array: labels,
          oIdx: i,
          nIdx: 0
        });
        label.group = 'Clients';
      } else if (label?.id === 'ef8154b0-7bda-4d1e-a87e-f286c06f7d94') {
        rearrangeArrayByIndex({
          array: labels,
          oIdx: i,
          nIdx: 1
        });
        label.group = 'Clients';
      } else if (label?.id === 'd583957c-3ee9-4ec2-b041-efa0f8d41875') {
        rearrangeArrayByIndex({
          array: labels,
          oIdx: i,
          nIdx: 2
        });
        label.group = 'Clients';
      } else if (label?.id === 'e542c9e7-ba5c-4ac3-9234-52263b157271') {
        rearrangeArrayByIndex({
          array: labels,
          oIdx: i,
          nIdx: 3
        });
        label.group = 'Clients';
      } else {
        label.group = 'Non-clients';
      }
    }
    return labels;
  };

  /**
   * Get age of dependent.
   *
   * @param {Object} dependent
   *
   * @returns {Number}
   */
  const getAgeOfDependent = ({ dependent }) => {
    if (dependent?.dateOfBirth !== 0) {
      const a = moment.utc();
      const b = moment.utc(dependent.dateOfBirth, 'X');

      const years = a.diff(b, 'year');
      const yStr = years > 0 ? `${years === 1 ? `${years} year` : `${years} years`}` : null;
      b.add(years, 'years');

      const months = a.diff(b, 'months');
      const mStr =
        months > 0 ? `${months === 1 ? `${months} month` : `${months} months`}` : '-';
      b.add(months, 'months');

      return _.compact([yStr, mStr]).join(', ');
    }
    return '-';
  };

  /**
   * Is client non borrowing spouse.
   *
   * @param {Array} ticketClientTypes
   * @param {String} workflow
   *
   * @returns {Boolean}
   */
  const isClientNonBorrowingSpouse = ({ ticketClientTypes, idContact }) => {
    const nonBorrowingSpouse = _.find(
      ticketClientTypes || [],
      (c) => c?.idContact === idContact
    );
    return nonBorrowingSpouse?.idLabel === getIdLabelsOfClient()[1];
  };

  /**
   * Is client security guarantor.
   *
   * @param {Array} ticketClientTypes
   * @param {String} workflow
   *
   * @returns {Boolean}
   */
  const isClientSecurityGuarantor = ({ ticketClientTypes, idContact }) => {
    const securityGuarantor = _.find(
      ticketClientTypes || [],
      (c) => c?.idContact === idContact
    );
    return securityGuarantor?.idLabel === getIdLabelsOfClient()[2];
  };

  /**
   * Is client servicing guarantor.
   *
   * @param {Array} ticketClientTypes
   * @param {String} workflow
   *
   * @returns {Boolean}
   */
  const isClientServicingGuarantor = ({ ticketClientTypes, idContact }) => {
    const servicingGuarantor = _.find(
      ticketClientTypes || [],
      (c) => c?.idContact === idContact
    );
    return servicingGuarantor?.idLabel === getIdLabelsOfClient()[3];
  };

  /**
   * Set ownership of asset or liability.
   *
   * @param {Array} contacts
   * @param {Boolean} isFirst
   *
   * @returns {Object}
   */
  const setOwnershipOfAssetOrLiability = (contacts, isFirst) => {
    const ownership = [];
    const percent = isFirst ? parseInt(100 / contacts.length, 10) : 0;
    for (let c = 0; c < contacts.length; c += 1) {
      ownership.push(Ownership.blank(contacts[c].id, percent));
    }
    return ownership;
  };

  /**
   * Get percent of ownership per contact.
   *
   * @param {Array} ownership
   * @param {String} idContact
   *
   * @returns {Number}
   */
  const getOwnershipPercentageOfAssetOrLiabilityPerContact = (ownership, idContact) => {
    let oP;
    if (ownership && ownership.length) {
      oP = ownership.find((o) => o.idContact === idContact);
    }
    return oP ? isNumber(oP.percent, false) : 0;
  };

  /**
   * Get ownerships.
   *
   * @param {Array} contacts
   * @param {Array} ownership
   *
   * @returns {Number}
   */
  const getOwnerships = ({ contacts, ownership }) => {
    const ownerships = [];
    const len = contacts?.length;
    for (let c = 0; c < len; c += 1) {
      const contact = contacts[c];
      const percent = getOwnershipPercentageOfAssetOrLiabilityPerContact(ownership, contact.id);
      ownerships.push(`${contact.getName()}: ${percent}%`);
    }
    return ownerships.join(', ');
  };

  /**
   * Edit ownership of asset or liability.
   *
   * @param {Event} event
   * @param {Object} item
   * @param {Array} contacts
   * @param {Boolean} isDisabled
   *
   * @returns {Object}
   */
  const editOwnershipOfAssetOrLiability = ({ event, item, contacts, isDisabled }) => {
    const deferred = $q.defer();
    const itemToEdit = item;
    if (!itemToEdit.ownership.length) {
      itemToEdit.ownership = setOwnershipOfAssetOrLiability(true, contacts);
    }
    $mdDialog
      .show({
        targetEvent: event,
        locals: {
          availableContacts: contacts
        },
        template,
        controllerAs: '$ctrl',
        controller(availableContacts) {
          const ctrl = this;
          ctrl.item = angular.copy(itemToEdit);
          ctrl.availableContacts = availableContacts;
          ctrl.isDisabled = isDisabled;

          ctrl.getItemName = () => {
            if (ctrl.item instanceof Asset) {
              return ctrl.item.getName() ? ctrl.item.getName() : ctrl.item.assetType.title;
            }
            if (ctrl.item instanceof Liability) {
              return ctrl.item.getName() ? ctrl.item.getName() : ctrl.item.liabilityType.title;
            }
            return '';
          };

          ctrl.getContact = (idContact) => {
            let contact;
            if (ctrl.availableContacts.length) {
              contact = ctrl.availableContacts.find((c) => c.id === idContact);
            }
            return contact;
          };

          ctrl.getTotalPercent = () =>
            ctrl.item.ownership.reduce((memo, num) => memo + num.percent, 0);

          ctrl.confirm = () => {
            $mdDialog.hide(ctrl.item);
          };
          ctrl.cancel = () => {
            $mdDialog.cancel();
          };
        }
      })
      .then((result) => deferred.resolve(result));
    return deferred.promise;
  };

  /**
   * Get percent of ownership for home loan security per contact.
   *
   * @param {Array} contacts
   *
   * @returns {Number}
   */
  const getOwnershipForNewProperties = (contacts) => {
    if (contacts && contacts.length) {
      return parseInt(100 / contacts.length, 10);
    }
    return 0;
  };

  /**
   * Is asset type owner occupier.
   *
   * @returns {Boolean}
   */
  const isAssetTypeOwnerOccupied = (assetType) =>
    assetType.id === Asset.getAvailableAssetTypes()[0].id;

  /**
   * Is asset type investment.
   *
   * @returns {Boolean}
   */
  const isAssetTypeInvestment = (assetType) =>
    assetType.id === Asset.getAvailableAssetTypes()[1].id;

  /**
   * Is asset type owner occupier or investment.
   *
   * @returns {Boolean}
   */
  const isAssetTypeOwnerOccupiedOrInvestment = (assetType) =>
    isAssetTypeOwnerOccupied(assetType) || isAssetTypeInvestment(assetType);

  /**
   * Is asset type vehicle.
   *
   * @returns {Boolean}
   */
  const isAssetTypeVehicle = (assetType) =>
    assetType.id === Asset.getAvailableAssetTypes()[2].id;

  /**
   * Is asset type bank accounts.
   *
   * @returns {Boolean}
   */
  const isAssetTypeBankAccount = (assetType) =>
    assetType.id === Asset.getAvailableAssetTypes()[3].id;

  /**
   * Is asset type home contents.
   *
   * @returns {Boolean}
   */
  const isAssetTypeHomeContents = (assetType) =>
    assetType.id === Asset.getAvailableAssetTypes()[4].id;

  /**
   * Is asset type kiwiSaver.
   *
   * @returns {Boolean}
   */
  const isAssetTypeKiwiSaver = (assetType) =>
    assetType.id === Asset.getAvailableAssetTypes()[5].id;

  /**
   * Is asset type superfund.
   *
   * @returns {Boolean}
   */
  const isAssetTypeSuperfund = (assetType) =>
    assetType.id === Asset.getAvailableAssetTypes()[6].id;

  /**
   * Is asset type shares.
   *
   * @returns {Boolean}
   */
  const isAssetTypeShares = (assetType) =>
    assetType.id === Asset.getAvailableAssetTypes()[7].id;

  /**
   * Is asset type other.
   *
   * @returns {Boolean}
   */
  const isAssetTypeOther = (assetType) => assetType.id === Asset.getAvailableAssetTypes()[8].id;

  /**
   * Is asset type balance sheet.
   *
   * @returns {Boolean}
   */
  const isAssetTypeBalanceSheet = (assetType) =>
    assetType.id === Asset.getAvailableAssetTypes()[9].id;

  /**
   * Get investment assets.
   *
   * @param {Array} assets
   * @param {Array} homeLoanSecurities
   *
   * @returns {Array}
   */
  const getOwnerOccupiedAssets = ({ assets, homeLoanSecurities }) => {
    const ownerOccupiedAssets = _.filter(assets, (a) => isAssetTypeOwnerOccupied(a.assetType));
    if (homeLoanSecurities && homeLoanSecurities.length) {
      const idAssets = _.compact(_.map(homeLoanSecurities, 'idAsset'));
      for (let i = 0; i < idAssets.length; i += 1) {
        const idx = findIndexById(ownerOccupiedAssets, idAssets[i]);
        if (idx > -1) {
          ownerOccupiedAssets.splice(idx, 1);
        }
      }
    }
    return ownerOccupiedAssets;
  };

  /**
   * Get investment assets.
   *
   * @param {Array} assets
   * @param {Array} homeLoanSecurities
   *
   * @returns {Array}
   */
  const getInvestmentAssets = ({ assets, homeLoanSecurities }) => {
    const investmentAssets = _.filter(assets, (a) => isAssetTypeInvestment(a.assetType));
    if (homeLoanSecurities && homeLoanSecurities.length) {
      const idAssets = _.compact(_.map(homeLoanSecurities, 'idAsset'));
      for (let i = 0; i < idAssets.length; i += 1) {
        const idx = findIndexById(investmentAssets, idAssets[i]);
        if (idx > -1) {
          investmentAssets.splice(idx, 1);
        }
      }
    }
    return investmentAssets;
  };

  /**
   * Get asset commitment schedule total.
   *
   * @param {Array} assetCommitmentSchedules
   * @param {String} field
   *
   * @returns {Object}
   */
  const getAssetCommitmentScheduleTotal = ({ assetCommitmentSchedules, field }) => {
    let total = 0;
    if (field && assetCommitmentSchedules?.length) {
      for (let i = 0; i < assetCommitmentSchedules.length; i += 1) {
        total += _.reduce(
          assetCommitmentSchedules[i]?.assetCommitments,
          (memo, num) => memo + (num.toBePaidOut ? 0 : isNumber(num[field], true)),
          0
        );
      }
    }
    return total;
  };

  /**
   * Is liability type mortgage loan.
   *
   * @returns {Boolean}
   */
  const isLiabilityTypeMortgage = (liabilityType) =>
    liabilityType.id === Liability.getAvailableLiabilityTypes()[0].id;

  /**
   * Is liability type credit card.
   *
   * @returns {Boolean}
   */
  const isLiabilityTypeCreditCard = (liabilityType) =>
    liabilityType.id === Liability.getAvailableLiabilityTypes()[1].id;

  /**
   * Is liability type overdraft.
   *
   * @returns {Boolean}
   */
  const isLiabilityTypeOverdraft = (liabilityType) =>
    liabilityType.id === Liability.getAvailableLiabilityTypes()[2].id;

  /**
   * Is liability type vehicle loan.
   *
   * @returns {Boolean}
   */
  const isLiabilityTypeVehicle = (liabilityType) =>
    liabilityType.id === Liability.getAvailableLiabilityTypes()[3].id;

  /**
   * Is liability type personal loan.
   *
   * @returns {Boolean}
   */
  const isLiabilityTypePersonal = (liabilityType) =>
    liabilityType.id === Liability.getAvailableLiabilityTypes()[4].id;

  /**
   * Is liability type SMSF loan.
   *
   * @returns {Boolean}
   */
  const isLiabilityTypeSmsf = (liabilityType) =>
    liabilityType.id === Liability.getAvailableLiabilityTypes()[5].id;

  /**
   * Is liability type education debt.
   *
   * @returns {Boolean}
   */
  const isLiabilityTypeEducationDebt = (liabilityType) =>
    liabilityType.id === Liability.getAvailableLiabilityTypes()[6].id;

  /**
   * Is liability type other.
   *
   * @returns {Boolean}
   */
  const isLiabilityTypeOther = (liabilityType) =>
    liabilityType.id === Liability.getAvailableLiabilityTypes()[7].id;

  /**
   * Is liability type balance sheet.
   *
   * @returns {Boolean}
   */
  const isLiabilityTypeBalanceSheet = (liabilityType) =>
    liabilityType.id === Liability.getAvailableLiabilityTypes()[8].id;

  /**
   * Is liability type loan.
   *
   * @returns {Boolean}
   */
  const isLiabilityTypeLoan = (liabilityType) =>
    isLiabilityTypeMortgage(liabilityType) ||
    isLiabilityTypeVehicle(liabilityType) ||
    isLiabilityTypePersonal(liabilityType) ||
    isLiabilityTypeSmsf(liabilityType) ||
    isLiabilityTypeOther(liabilityType);

  /**
   * Is liability type mortgage or vehicle or SMSF loan.
   *
   * @returns {Boolean}
   */
  const isLiabilityTypeMortgageOrVehicleOrSmsf = (liabilityType) =>
    isLiabilityTypeMortgage(liabilityType) ||
    isLiabilityTypeVehicle(liabilityType) ||
    isLiabilityTypeSmsf(liabilityType);

  /**
   * Is liability type mortgage or SMSF loan.
   *
   * @returns {Boolean}
   */
  const isLiabilityTypeMortgageOrSmsfLoan = (liabilityType) =>
    isLiabilityTypeMortgage(liabilityType) || isLiabilityTypeSmsf(liabilityType);

  /**
   * Has education debt.
   *
   * @param {Array} liabilities
   * @param {String} idContact
   *
   * @returns {Number}
   */
  const hasEducationDebtPerContact = ({ liabilities, idContact }) => {
    const allLiabilityTypes = Liability.getAvailableLiabilityTypes();
    const currentLiabilities = [];
    const filteredLiabilities = _.filter(
      liabilities || [],
      (l) => l?.liabilityType?.id === allLiabilityTypes[6]?.id && !l?.isRefinanced
    );
    for (let l = 0; l < filteredLiabilities?.length; l += 1) {
      const liability = filteredLiabilities[l];
      const percent = getOwnershipPercentageOfAssetOrLiabilityPerContact(
        liability?.ownership,
        idContact
      );
      if (percent > 0) {
        currentLiabilities.push(liability);
      }
    }
    return currentLiabilities.length > 0;
  };

  /**
   * Get total liabilities existing repayment.
   *
   * @param {Array} assets
   * @param {String} idAsset
   *
   * @returns {Number}
   */
  const isLiabilityLinkedToAssetMarkedAsToBeSold = ({ assets, idAsset }) => {
    const asset = _.find(assets || [], (a) => a?.id === idAsset && a?.toBeSold);
    return asset !== undefined;
  };

  /**
   * Get total liabilities existing repayment.
   *
   * @param {Array} assets
   * @param {Array} liabilities
   * @param {Array} assetCommitmentSchedules
   * @param {Array} contacts
   * @param {Object} workflow
   * @param {Boolean} isCurrent
   *
   * @returns {Number}
   */
  const getTotalExistingLiabilitiesRepayments = ({
    assets,
    liabilities,
    assetCommitmentSchedules,
    contacts,
    workflow,
    isCurrent
  }) => {
    let total = 0;
    let repayment = 0;
    const idContacts = _.map(contacts, 'id');
    const lLen = liabilities?.length || 0;
    for (let l = 0; l < lLen; l += 1) {
      const liability = liabilities[l];
      const cLen = idContacts?.length || 0;
      for (let c = 0; c < cLen; c += 1) {
        const ownership = findByIdContact({
          array: liability?.ownership || [],
          idContact: idContacts[c]
        });
        if (ownership) {
          const ownershipPercentage = _.divide(isNumber(ownership?.percent || 0, true), 100);
          if (isCurrent) {
            if (isLiabilityTypeCreditCard(liability?.liabilityType)) {
              repayment = _.multiply(
                isNumber(liability?.limit || 0, true),
                getAvailableRepaymentIndexes({ workflow })?.creditCardRate || 1
              );
            } else if (isLiabilityTypeOverdraft(liability?.liabilityType)) {
              repayment = _.multiply(
                isNumber(liability?.limit, true),
                getAvailableRepaymentIndexes({ workflow })?.overdraftRate || 1
              );
            } else {
              repayment = isNumber(liability?.repayment || 0, true);
            }
            total += _.multiply(repayment, ownershipPercentage);
          } else if (!isCurrent && !liability?.isRefinanced) {
            if (isLiabilityTypeCreditCard(liability?.liabilityType)) {
              repayment = _.multiply(
                isNumber(liability?.limit || 0, true),
                getAvailableRepaymentIndexes({ workflow })?.creditCardRate || 1
              );
            } else if (isLiabilityTypeOverdraft(liability?.liabilityType)) {
              repayment = _.multiply(
                isNumber(liability?.limit || 0, true),
                getAvailableRepaymentIndexes({ workflow })?.overdraftRate || 1
              );
            } else if (
              isLiabilityTypeMortgage(liability?.liabilityType) &&
              !isLiabilityLinkedToAssetMarkedAsToBeSold({
                assets,
                idAsset: liability?.idAsset
              })
            ) {
              repayment = isNumber(liability?.repayment || 0, true);
            } else if (!isLiabilityTypeMortgage(liability?.liabilityType)) {
              repayment = isNumber(liability?.repayment || 0, true);
            }
            total += _.multiply(repayment, ownershipPercentage);
          }
        }
      }
    }
    const filteredAssetCommitmentSchedules = _.filter(
      assetCommitmentSchedules,
      (a) => idContacts.indexOf(a?.idContact) > -1
    );
    if (filteredAssetCommitmentSchedules?.length) {
      total += getAssetCommitmentScheduleTotal({
        assetCommitmentSchedules: filteredAssetCommitmentSchedules,
        field: 'repayments'
      });
    }
    return total;
  };

  /**
   * Get existing repayment.
   *
   * @param {Object} liability
   * @param {Number} buffer
   *
   * @returns {Number}
   */
  const getExistingRepayment = ({ liability, buffer }) => {
    let repayment = 0;
    if (liability?.limit || liability?.balance) {
      const principal =
        liability?.limit > liability?.balance ? liability.limit : liability?.balance || 0;
      const term = liability?.endDate
        ? moment(liability?.endDate, 'X').diff(moment(), 'years') * 12
        : 300;
      const rate = liability?.interestRate
        ? (liability?.interestRate + buffer) / 100 / 12
        : (4 + buffer) / 100 / 12;
      repayment = ticketFactFindService.getPayment(rate, term, principal);
    }
    return repayment;
  };

  /**
   * Get total existing liabilities repayment for proposed position at buffer rate.
   *
   * @param {Array} assets
   * @param {Array} liabilities
   * @param {Array} contacts
   * @param {Object} workflow
   *
   * @returns {Number}
   */
  const getTotalProposedExistingRepaymentsAtBufferRate = ({
    assets,
    liabilities,
    contacts,
    workflow
  }) => {
    let hasExistingPrimaryBuffer = true;
    if (workflow?.isAssetFinance()) {
      hasExistingPrimaryBuffer =
        $rootScope.CurrentOrganization?.hasPrimaryExistingRepaymentBufferAF();
    }
    if (workflow?.isHomeLoan()) {
      hasExistingPrimaryBuffer =
        $rootScope.CurrentOrganization?.hasPrimaryExistingRepaymentBufferHL();
    }
    const currentRepaymentPrimaryBuffer = getPrimaryBuffer({
      workflow,
      bufferType: 'EXISTING_REPAYMENT'
    });
    const currentRepaymentSecondaryBuffer = getSecondaryBuffer({
      workflow,
      bufferType: 'EXISTING_REPAYMENT'
    });
    let total = 0;
    let repayment = 0;
    const filteredLiabilities = _.filter(liabilities || [], (l) => !l?.isRefinanced);
    const lLen = filteredLiabilities?.length || 0;
    for (let l = 0; l < lLen; l += 1) {
      const liability = filteredLiabilities[l];
      const cLen = contacts?.length || 0;
      for (let c = 0; c < cLen; c += 1) {
        const ownership = findByIdContact({
          array: liability?.ownership || [],
          idContact: contacts[c]?.id
        });
        if (ownership) {
          const ownershipPercentage = _.divide(isNumber(ownership?.percent || 0, true), 100);
          if (isLiabilityTypeCreditCard(liability?.liabilityType)) {
            repayment = _.multiply(
              isNumber(liability?.limit || 0, true),
              getAvailableRepaymentIndexes({ workflow })?.creditCardRate || 1
            );
          } else if (isLiabilityTypeOverdraft(liability?.liabilityType)) {
            repayment = _.multiply(
              isNumber(liability?.limit || 0, true),
              getAvailableRepaymentIndexes({ workflow })?.overdraftRate || 1
            );
          } else if (hasExistingPrimaryBuffer) {
            if (
              isLiabilityTypeMortgage(liability?.liabilityType) &&
              !isLiabilityLinkedToAssetMarkedAsToBeSold({
                assets,
                idAsset: liability?.idAsset
              })
            ) {
              repayment = isNumber(liability?.repayment || 0, true)
                ? _.multiply(
                    isNumber(liability?.repayment || 0, true),
                    currentRepaymentPrimaryBuffer
                  )
                : _.multiply(
                    getExistingRepayment({
                      liability,
                      buffer: 0
                    }),
                    currentRepaymentPrimaryBuffer
                  );
            } else if (!isLiabilityTypeMortgage(liability?.liabilityType)) {
              repayment = isNumber(liability?.repayment || 0, true);
            }
          } else if (!hasExistingPrimaryBuffer) {
            if (
              isLiabilityTypeMortgage(liability?.liabilityType) &&
              !isLiabilityLinkedToAssetMarkedAsToBeSold({
                assets,
                idAsset: liability?.idAsset
              })
            ) {
              repayment = getExistingRepayment({
                liability,
                buffer: currentRepaymentSecondaryBuffer
              });
            } else if (!isLiabilityTypeMortgage(liability?.liabilityType)) {
              repayment = isNumber(liability?.repayment || 0, true);
            }
          }
          total += _.multiply(repayment, ownershipPercentage);
        }
      }
    }
    return total;
  };

  /**
   * Get total existing liabilities repayment for proposed position at buffer rate.
   *
   * @param {Object} workflow
   * @param {Object} item
   * @param {Number} hasBuffer
   *
   * @returns {Number}
   */
  const getTotalProposedLiabilitiesRepayments = ({ workflow, item, hasBuffer }) => {
    let out = 0;
    if (workflow?.isHomeLoan()) {
      out = hasBuffer
        ? item?.repayments?.ongoingMonthlyRepaymentBuffer || 0
        : item?.repayments?.ongoingMonthlyRepayment || 0;
    }
    if (workflow?.isAssetFinance()) {
      out = hasBuffer ? item?.estimatedRepaymentBuffer || 0 : item?.estimatedRepayment || 0;
    }
    return out;
  };

  /**
   * Get security by ID.
   *
   * @param {Array} homeLoanSecurities
   * @param {String} idSecurity
   *
   * @returns {Object}
   */
  const getSecurityById = ({ homeLoanSecurities, idSecurity }) =>
    _.find(homeLoanSecurities, (s) => s?.id === idSecurity);

  /**
   * Get investment home loan securities.
   *
   * @param {Array} homeLoanSecurities
   *
   * @returns {Array}
   */
  const getInvestmentHomeLoanSecurities = ({ homeLoanSecurities }) =>
    _.filter(
      homeLoanSecurities,
      (s) => s?.ownershipType?.name === OwnershipType.investment.name && s?.ownership?.length
    );

  /**
   * Get investment home loan securities.
   *
   * @param {Object} contact
   *
   * @returns {Boolean}
   */
  const isSelfEmployedInACompany = ({ contact }) =>
    _.some(
      contact?.person?.employments || [],
      (emp) =>
        emp?.isCurrent &&
        emp?.type?.name === ContactEmploymentType.self_employed.name &&
        emp?.basis?.name === ContactEmploymentBasis.in_a_company.name
    );

  /**
   * Get purchase owner occupied status of home loan security.
   *
   * @param {Array} homeLoanSecurities
   * @param {Object} contact
   *
   * @returns {Boolean}
   */
  const getPurchaseOwnerOccupiedStatusOfHomeLoanSecurity = ({ homeLoanSecurities, contact }) =>
    _.some(
      _.filter(
        homeLoanSecurities,
        (h) =>
          getOwnershipPercentageOfAssetOrLiabilityPerContact(h?.ownership || [], contact?.id) >
          0
      ),
      (s) =>
        s?.ownershipType?.name === OwnershipType.owner_occupied.name &&
        s?.propertyType?.name !== PropertyType.holiday_home.name &&
        s?.propertyType?.name !== PropertyType.holiday_rental.name &&
        s?.propertyType?.name !== PropertyType.vacant_land.name
    );

  /**
   * Get purchase owner occupied status.
   *
   * @param {Array} contacts
   * @param {Array} assets
   * @param {Array} homeLoanSecurities
   * @param {Array} homeLoanFundingWorksheets
   *
   * @returns {Boolean}
   */
  const getPurchaseOwnerOccupiedStatus = ({
    contacts,
    assets,
    homeLoanSecurities,
    homeLoanFundingWorksheets
  }) => {
    const collection = [];
    const len = contacts?.length || 0;
    for (let c = 0; c < len; c += 1) {
      const contact = contacts[c];
      collection.push(
        contact?.person?.contact?.settlementHousing?.name !==
          ContactResidentialStatusOption.none.name &&
          contact?.person?.contact?.settlementHousing?.name !==
            ContactResidentialStatusOption.renting.name &&
          contact?.person?.contact?.settlementHousing?.name !==
            ContactResidentialStatusOption.boarding.name
      );
      if (homeLoanSecurities && homeLoanSecurities?.length) {
        collection.push(
          getPurchaseOwnerOccupiedStatusOfHomeLoanSecurity({ homeLoanSecurities, contact })
        );
      }
      if (assets && assets?.length) {
        collection.push(
          _.some(
            _.filter(
              assets,
              (obj) =>
                getOwnershipPercentageOfAssetOrLiabilityPerContact(
                  obj?.ownership || [],
                  contact?.id
                ) > 0
            ),
            (a) =>
              (isAssetTypeOwnerOccupied(a?.assetType) ||
                (isAssetTypeInvestment(a?.assetType) && a?.willItBecomeOwnerOccupied)) &&
              !a?.toBeSold
          )
        );
      }
      if (
        homeLoanFundingWorksheets &&
        !_.isEmpty(homeLoanFundingWorksheets) &&
        homeLoanFundingWorksheets?.fundsRequired?.length
      ) {
        const fundsRequired = [];
        const fLen = homeLoanFundingWorksheets?.fundsRequired?.length || 0;
        for (let i = 0; i < fLen; i += 1) {
          const fundRequired = homeLoanFundingWorksheets?.fundsRequired[i];
          const security = getSecurityById({
            homeLoanSecurities: homeLoanSecurities || [],
            idSecurity: fundRequired?.idSecurity
          });
          if (
            getOwnershipPercentageOfAssetOrLiabilityPerContact(
              security?.ownership || [],
              contact?.id
            ) > 0
          ) {
            fundsRequired.push(fundRequired);
          }
        }
        collection.push(
          _.some(
            fundsRequired,
            (f) => f?.ownershipType?.name === OwnershipType.owner_occupied.name
          )
        );
      }
    }
    return _.some(collection, (c) => c === true);
  };

  /**
   * Get purchase owner occupied status.
   *
   * @param {Array} assets
   * @param {Array} homeLoanSecurities
   * @param {Object} contact
   * @param {Object} owner
   *
   * @returns {Object}
   */
  const getStateAndPostCodeByContact = ({ assets, homeLoanSecurities, contact, owner }) => {
    const orgCca2 = getOrganizationCountry().cca2;
    const statesAU = getAvailableAddressStates();
    let state;
    let postCode;
    if (
      !state &&
      !postCode &&
      contact &&
      contact?.person?.contact?.settlementAddress?.state &&
      contact?.person?.contact?.settlementAddress?.postCode
    ) {
      state = contact?.person?.contact?.settlementAddress?.state;
      postCode = contact?.person?.contact?.settlementAddress?.postCode;
    }
    if (!state && !postCode && homeLoanSecurities && homeLoanSecurities?.length) {
      let securitiesOO = _.filter(
        _.filter(
          homeLoanSecurities,
          (h) =>
            getOwnershipPercentageOfAssetOrLiabilityPerContact(
              h?.ownership || [],
              contact?.id
            ) > 0
        ),
        (s) =>
          s?.ownershipType?.name === OwnershipType.owner_occupied.name &&
          s?.propertyType !== 10 &&
          s?.propertyType !== 11 &&
          s?.propertyType !== 13
      );
      if (orgCca2 === 'AU') {
        securitiesOO = _.filter(securitiesOO, (s) => s?.address?.state && s?.address?.postCode);
      }
      if (securitiesOO.length) {
        state = securitiesOO[0].address.state;
        postCode = securitiesOO[0].address.postCode;
      }
    }
    if (!state && !postCode && assets && assets?.length) {
      let assetsOO = _.filter(
        _.filter(
          assets,
          (obj) =>
            getOwnershipPercentageOfAssetOrLiabilityPerContact(
              obj?.ownership || [],
              contact?.id
            ) > 0
        ),
        (a) =>
          (isAssetTypeOwnerOccupied(a?.assetType) ||
            (isAssetTypeInvestment(a?.assetType) && a?.willItBecomeOwnerOccupied)) &&
          !a?.toBeSold
      );
      if (orgCca2 === 'AU') {
        assetsOO = _.filter(assetsOO, (s) => s?.address?.state && s?.address?.postCode);
      }
      if (assetsOO.length) {
        state = assetsOO[0].address.state;
        postCode = assetsOO[0].address.postCode;
      }
    }
    if (
      !state &&
      !postCode &&
      contact &&
      contact?.person?.contact?.address?.state &&
      contact?.person?.contact?.address?.postCode
    ) {
      state = contact.person.contact.address.state;
      postCode = contact.person.contact.address.postCode;
    }
    if (
      !state &&
      !postCode &&
      owner &&
      owner?.user?.address?.state &&
      owner?.user?.address?.postCode
    ) {
      state = owner.user.address.state;
      postCode = owner.user.address.postCode;
    }
    if (
      !state &&
      !postCode &&
      $rootScope.CurrentOrganization?.organization?.address?.state &&
      $rootScope.CurrentOrganization?.organization?.address?.postCode
    ) {
      state = $rootScope.CurrentOrganization.organization.address.state;
      postCode = $rootScope.CurrentOrganization.organization.address.postCode;
    }
    if (!state && !postCode && orgCca2 === 'AU') {
      state = statesAU[2].id;
      postCode = 2000;
    }
    if (!state && !postCode && orgCca2 === 'NZ') {
      state = 'ANY';
      postCode = 0;
    }
    return { state, postCode };
  };

  /**
   * Get index by contact ID.
   *
   * @param {Array} array
   * @param {Number/String} id
   *
   * @returns {Number}
   */
  const findIndexByContactId = (array, id) => array.findIndex((item) => item.idContact === id);

  /**
   * Sort array of objects based on another array.
   *
   * @param {Array} array
   * @param {Array} order
   * @param {String} type
   * @param {String} key
   *
   * @returns {Array}
   */
  const sortArrayBasedOnAnotherArray = ({ array, order, type, key }) => {
    _.sortBy(order, key);
    const orderArray = _.map(order, key);
    array.sort((a, b) => {
      const objA = a[type][key];
      const objB = b[type][key];
      if (orderArray.indexOf(objA) > orderArray.indexOf(objB)) {
        return 1;
      }
      return -1;
    });
    return array;
  };

  /**
   * Get frequency value.
   *
   * @param {Number} value
   * @param {Boolean} isFloat
   *
   * @returns {Number}
   */
  const getFrequencyValue = (value, isExpense) => {
    let out = 1;
    switch (value.name) {
      case 'weekly':
        out = isExpense ? 4.3333333333 : 52;
        break;
      case 'fortnightly':
        out = isExpense ? 2.1666666667 : 26;
        break;
      case 'monthly':
        out = isExpense ? 1 : 12;
        break;
      case 'quarterly':
        out = isExpense ? 0.3333333333 : 4;
        break;
      case 'semiannual':
        out = isExpense ? 0.1666666667 : 2;
        break;
      case 'annually':
        out = isExpense ? 0.0833333333 : 1;
        break;
      default:
        break;
    }
    return out;
  };

  /**
   * Is state a valid.
   *
   * @param {String} currentState
   *
   * @returns {Number}
   */
  const isStateValid = (currentState) => {
    const state = _.find(getAvailableAddressStates(), (s) => s.id === currentState);
    return state !== undefined;
  };

  /**
   * Is Valid ABN
   *
   * @param {String} abn
   *
   * @returns {boolean}
   */
  const isValidAbn = (abn) => {
    const withRemovedSpace = String(abn).trim().replace(/\s/g, '');

    const isNZ = getOrganizationCountry().cca2 === 'NZ';
    if (isNZ) {
      return /^\d{13}$/.test(withRemovedSpace);
    }
    return /^\d{11}$/.test(withRemovedSpace);
  };

  /**
   * Remove item from array.
   *
   * @param {Array} array
   * @param {Number} index
   *
   * @returns {Array}
   */
  const removeItemFromArray = (array, idx) => {
    array.splice(idx, 1);
    return array;
  };

  /**
   * Delete item.
   *
   * @param {Array} collection
   * @param {Object} item
   * @param {String} toastP1
   * @param {String} toastP2
   * @param {Function} method
   *
   */
  const deleteItem = (collection, item, toastP1, toastP2) => {
    const deferred = $q.defer();
    const idx = collection.findIndex((i) => i.id === item.id);
    if (idx > -1) {
      const deleteTimeout = $timeout(() => {
        collection.splice(idx, 1);
        deferred.resolve(true);
        uiService.toast({
          text: {
            p1: toastP1,
            p2: toastP2 || '',
            p3: 'has been deleted'
          },
          warn: true
        });
      }, 5250);
      uiService
        .toast({
          text: {
            p1: toastP1,
            p2: `${toastP2 ? `${toastP2} ` : ''}will be`,
            p3: 'deleted'
          },
          warn: true,
          action: 'UNDO'
        })
        .then((action) => {
          if (action) {
            $timeout.cancel(deleteTimeout);
            uiService.toast({
              text: {
                p1: toastP1,
                p2: `${toastP2 ? `${toastP2} ` : ''}deletion`,
                p3: 'undone'
              }
            });
            deferred.reject();
          }
        });
    }
    return deferred.promise;
  };

  /**
   * Get contact.
   *
   * @param {Array} contacts
   * @param {Number} idContact
   *
   * @returns {Object}
   */
  const getContact = ({ contacts, idContact }) =>
    _.find(contacts, (obj) => obj.id === idContact);

  /**
   * Get contacts by content type.
   *
   * @param {Array} contacts
   * @param {Array} idContacts
   * @param {Array} idEmployers
   * @param {String} contentType
   *
   * @returns {Object}
   */
  const getContactsByContentType = ({ contacts, idContacts, idEmployers, contentType }) => {
    let content = '-';
    const contactIds = idContacts || [];
    const employerIds = idEmployers || [];
    const len = contacts?.length || 0;
    if (contacts?.length) {
      switch (contentType) {
        case 'name': {
          const names = [];
          for (let c = 0; c < len; c += 1) {
            const contact = contacts[c];
            if (contactIds.indexOf(contact.id) > -1) {
              names.push(contact.getName());
            }
          }
          content = names.length ? names.join(', ') : '-';
          break;
        }
        case 'employer': {
          const employers = [];
          for (let c = 0; c < len; c += 1) {
            const contact = contacts[c];
            if (!contact.isCompany) {
              const eLen = contact?.person?.employments?.length || 0;
              for (let e = 0; e < eLen; e += 1) {
                const employment = contact.person.employments[e];
                if (employerIds.indexOf(employment.id) > -1) {
                  employers.push(employment.employerName);
                }
              }
            }
          }
          content = employers.length ? employers.join(', ') : '-';
          break;
        }
        default:
          content = '-';
          break;
      }
    }
    return content;
  };

  /**
   * Get contact type in deal.
   *
   * @param {Object} ticket
   * @param {Array} contactLabels
   * @param {String} idContact
   * @param {Boolean} isDealContactType
   *
   * @returns {String}
   */
  const getContactTypeInDeal = ({ ticket, contactLabels, idContact, isDealContactType }) => {
    const contactType = _.find(ticket?.ticketClientTypes, (obj) => obj.idContact === idContact);
    const label = _.find(contactLabels, (obj) => obj.id === contactType?.idLabel);
    if (
      label?.name?.toLowerCase() === 'security guarantor' ||
      label?.name?.toLowerCase() === 'servicing guarantor'
    ) {
      return isDealContactType ? label?.name : 'Guarantor';
    }
    if (label?.name?.toLowerCase() === 'client') {
      return isDealContactType ? label?.name : 'Borrower';
    }
    if (label?.name?.toLowerCase() === 'non-borrowing spouse') {
      return label?.name;
    }
    return '-';
  };

  /**
   * Get preferred contact method.
   *
   * @param {Number} idTitle
   *
   * @returns {String}
   */
  const getPreferredContactMethod = (preferredMethod) => {
    const method = [];
    if (preferredMethod?.email) {
      method.push('Email');
    }
    if (preferredMethod?.phone) {
      method.push('Phone');
    }
    return method.join(', ') || '-';
  };

  /**
   * Get linked asset.
   *
   * @param {Array} assets
   * @param {String} idAsset
   *
   * @returns {String}
   */
  const getLinkedAsset = ({ assets, idAsset }) => {
    const asset = _.find(assets || [], (obj) => obj.id === idAsset);
    return asset ? asset.getName() || '-' : '-';
  };

  /**
   * Get linked insurance.
   *
   * @param {Array} insurance
   * @param {String} idInsurance
   *
   * @returns {String}
   */
  const getLinkedInsurance = ({ insurance, idInsurance }) => {
    const insuranceNames = [];
    const insuranceIds = idInsurance || [];
    const len = insurance?.length || 0;
    for (let i = 0; i < len; i += 1) {
      const ins = insurance[i];
      if (insuranceIds.indexOf(ins.id) > -1) {
        insuranceNames.push(ins.getName());
      }
    }
    return insuranceNames.length ? insuranceNames.join(', ') : '-';
  };

  /**
   * Get lender by abbr.
   *
   * @param {Array} lenders
   * @param {String} lenderId
   * @param {String} lenderAbbr
   * @param {String} lenderOtherName
   * @param {String} contentType
   *
   * @returns {String}
   */
  const getLenderByContentType = ({
    lenders,
    lenderId,
    lenderAbbr,
    lenderOtherName,
    contentType
  }) => {
    const lender = _.find(lenders, (obj) =>
      contentType === 'id' ? obj.id === lenderId : obj.abbr === lenderAbbr.toUpperCase()
    );
    if (
      contentType === 'abbr' &&
      (lender?.name === 'Other' || (lenderAbbr && lenderAbbr === 'other'))
    ) {
      return lenderOtherName || '-';
    }
    return lender ? lender?.name || '-' : '-';
  };

  /**
   * Get home loan security contact for valuation type.
   *
   * @param {Number} idContactForValuationType
   *
   * @returns {String}
   */
  const getHomeLoanSecurityContactForValuationType = (idContactForValuationType) => {
    const contactForValuationType = _.find(
      getAvailableOtherContactTypesForValuation(),
      (obj) => obj.id === idContactForValuationType
    );
    return contactForValuationType?.id ? contactForValuationType?.value || '-' : '-';
  };

  /**
   * Get linked insurance.
   *
   * @param {Array} securities
   * @param {String} idSecurity
   *
   * @returns {String}
   */
  const getLinkedHomeLoanSecurity = ({ securities, idSecurity }) => {
    const security = _.find(securities || [], (obj) => obj.id === idSecurity);
    return security ? Address.getAddress(security?.address) || '-' : '-';
  };

  /**
   * Is property used as security.
   *
   * @param {String} idAsset
   * @param {Array} securities
   *
   * @returns {Object}
   */
  const isPropertyUsedAsSecurity = ({ idAsset, securities }) =>
    _.some(securities || [], (s) => s.idAsset === idAsset);

  /**
   * Is liability used in home loan.
   *
   * @param {Array} homeLoanOtherMortgages
   * @param {String} idLiability
   * @param {Boolean} isDealOwnerBroker
   * @param {Boolean} haveLendersExisting
   *
   * @returns {Boolean}
   */
  const isLiabilityUsedInHomeLoan = (
    homeLoanOtherMortgages,
    idLiability,
    isDealOwnerBroker,
    haveLendersExisting
  ) => {
    if (
      isDealOwnerBroker &&
      $rootScope.hasBrokerToolsEnabled() &&
      haveLendersExisting &&
      homeLoanOtherMortgages &&
      homeLoanOtherMortgages.length
    ) {
      return _.some(homeLoanOtherMortgages, (l) => l.idLiability === idLiability);
    }
    return false;
  };

  /**
   * Get asset finance product type.
   *
   * @param {Number} idProductType
   *
   * @returns {String}
   */
  const getAssetFinanceProductType = (idProductType) => {
    const assetFinanceProductType = _.find(
      ASSET_FINANCE.DETAILS.PRODUCT_TYPE,
      (obj) => obj.id === idProductType
    );
    return assetFinanceProductType?.id ? assetFinanceProductType?.value || '-' : '-';
  };

  /**
   * Get asset finance asset type.
   *
   * @param {Number} idAssetType
   *
   * @returns {String}
   */
  const getAssetFinanceAssetType = (idAssetType) => {
    const assetType = _.find(ASSET_FINANCE.DETAILS.ASSET_TYPE, (obj) => obj.id === idAssetType);
    return assetType?.id ? assetType?.value || '-' : '-';
  };

  /**
   * Get asset finance asset age.
   *
   * @param {Number} idAssetAge
   *
   * @returns {String}
   */
  const getAssetFinanceAssetAge = (idAssetAge) => {
    const assetAge = _.find(ASSET_FINANCE.DETAILS.ASSET_AGE, (obj) => obj.id === idAssetAge);
    return assetAge?.id ? assetAge?.value || '-' : '-';
  };

  /**
   * Get asset finance vendor type.
   *
   * @param {Number} idVendorType
   *
   * @returns {String}
   */
  const getAssetFinanceVendorType = (idVendorType) => {
    const vendorType = _.find(
      ASSET_FINANCE.DETAILS.VENDOR_TYPE,
      (obj) => obj.id === idVendorType
    );
    return vendorType?.id ? vendorType?.value || '-' : '-';
  };

  /**
   * Get asset finance payment frequency.
   *
   * @param {Number} idPaymentFrequency
   *
   * @returns {String}
   */
  const getAssetFinancePaymentFrequency = (idPaymentFrequency) => {
    const paymentFrequency = _.find(
      ASSET_FINANCE.DETAILS.PAYMENT_FREQUENCY,
      (obj) => obj.id === idPaymentFrequency
    );
    return paymentFrequency?.id ? paymentFrequency?.value || '-' : '-';
  };

  /**
   * Get available objective product requirements pay balloon options.
   *
   * @returns {Array}
   */
  const getAvailableObjectivePayBallonOptions = () =>
    _.without(ObjectivePayBallonOption.available(), ObjectivePayBallonOption.none);

  /**
   * Is address existed.
   *
   * @param {Array} addresses
   * @param {Object} address
   *
   * @returns {Boolean}
   */
  const isAddressExisted = ({ addresses, address }) => {
    const newAddress = Address.getAddress(address);
    return addresses.find((a) => {
      const existAddress = Address.getAddress(a);
      return JSON.stringify(existAddress) === JSON.stringify(newAddress);
    });
  };

  /**
   * Get all addresses.
   *
   * @returns {Boolean}
   */
  const getAllAddresses = (
    contacts,
    assets,
    otherAdvisers,
    ticketOwner,
    workflow,
    isDealOwnerBroker,
    assetFinanceCompliance,
    homeLoanSecurityDetails,
    homeLoanCompliance,
    riskInsuranceCompliance
  ) => {
    const addresses = [];
    for (let c = 0; c < contacts.length; c += 1) {
      const contact = contacts[c];
      if (contact.isCompany) {
        if (_.get(contact, 'company.contact.address')) {
          const address = _.get(contact, 'company.contact.address');
          if (Address.hasAddress(address) && !isAddressExisted({ addresses, address })) {
            addresses.push(Address.build(address));
          }
        }
        if (_.get(contact, 'company.contact.mailingAddress')) {
          const address = _.get(contact, 'company.contact.mailingAddress');
          if (Address.hasAddress(address) && !isAddressExisted({ addresses, address })) {
            addresses.push(Address.build(address));
          }
        }
      } else {
        if (_.get(contact, 'person.information.nextOfKinAddress')) {
          const address = _.get(contact, 'person.information.nextOfKinAddress');
          if (Address.hasAddress(address) && !isAddressExisted({ addresses, address })) {
            addresses.push(Address.build(address));
          }
        }
        if (_.get(contact, 'person.information.personalReferenceAddress1')) {
          const address = _.get(contact, 'person.information.personalReferenceAddress1');
          if (Address.hasAddress(address) && !isAddressExisted({ addresses, address })) {
            addresses.push(Address.build(address));
          }
        }
        if (_.get(contact, 'person.information.personalReferenceAddress2')) {
          const address = _.get(contact, 'person.information.personalReferenceAddress2');
          if (Address.hasAddress(address) && !isAddressExisted({ addresses, address })) {
            addresses.push(Address.build(address));
          }
        }
        if (_.get(contact, 'person.contact.address')) {
          const address = _.get(contact, 'person.contact.address');
          if (Address.hasAddress(address) && !isAddressExisted({ addresses, address })) {
            addresses.push(Address.build(address));
          }
        }
        if (_.get(contact, 'person.contact.previousAddress')) {
          const address = _.get(contact, 'person.contact.previousAddress');
          if (Address.hasAddress(address) && !isAddressExisted({ addresses, address })) {
            addresses.push(Address.build(address));
          }
        }
        if (_.get(contact, 'person.contact.mailingAddress')) {
          const address = _.get(contact, 'person.contact.mailingAddress');
          if (Address.hasAddress(address) && !isAddressExisted({ addresses, address })) {
            addresses.push(Address.build(address));
          }
        }
        if (_.get(contact, 'person.contact.settlementAddress')) {
          const address = _.get(contact, 'person.contact.settlementAddress');
          if (Address.hasAddress(address) && !isAddressExisted({ addresses, address })) {
            addresses.push(Address.build(address));
          }
        }
        for (let e = 0; e < contact.person.employments.length; e += 1) {
          const employment = contact.person.employments[e];
          if (_.get(employment, 'employerAddress')) {
            const address = _.get(employment, 'employerAddress');
            if (Address.hasAddress(address) && !isAddressExisted({ addresses, address })) {
              addresses.push(Address.build(address));
            }
          }
        }
      }
    }

    for (let a = 0; a < assets.length; a += 1) {
      const asset = assets[a];
      if (_.get(asset, 'address')) {
        const address = _.get(asset, 'address');
        if (Address.hasAddress(address) && !isAddressExisted({ addresses, address })) {
          addresses.push(Address.build(address));
        }
      }
    }

    for (let o = 0; o < otherAdvisers.length; o += 1) {
      const otherAdviser = otherAdvisers[o];
      if (_.get(otherAdviser, 'address')) {
        const address = _.get(otherAdviser, 'address');
        if (Address.hasAddress(address) && !isAddressExisted({ addresses, address })) {
          addresses.push(Address.build(address));
        }
      }
    }

    if (
      Address.hasAddress(ticketOwner?.user?.address) &&
      !isAddressExisted({ addresses, address: ticketOwner?.user?.address })
    ) {
      addresses.push(Address.build(ticketOwner.user.address));
    }

    if (
      Address.hasAddress($rootScope.CurrentOrganization?.organization?.address) &&
      !isAddressExisted({
        addresses,
        address: $rootScope.CurrentOrganization.organization.address
      })
    ) {
      addresses.push(Address.build($rootScope.CurrentOrganization.organization.address));
    }

    if (isDealOwnerBroker && $rootScope.hasBrokerToolsEnabled()) {
      if (
        workflow?.isAssetFinance() &&
        assetFinanceCompliance?.verifications?.applicants?.length
      ) {
        for (let a = 0; a < assetFinanceCompliance.verifications.applicants.length; a += 1) {
          const applicant = assetFinanceCompliance.verifications.applicants[a];
          if (_.get(applicant, 'address')) {
            const address = _.get(applicant, 'address');
            if (Address.hasAddress(address) && !isAddressExisted({ addresses, address })) {
              addresses.push(Address.build(address));
            }
          }
        }
      }
      if (
        workflow?.isAssetFinance() &&
        Address.hasAddress(assetFinanceCompliance?.verifications?.interviewDetailsLocation) &&
        !isAddressExisted({
          addresses,
          address: assetFinanceCompliance?.verifications?.interviewDetailsLocation
        })
      ) {
        addresses.push(
          Address.build(assetFinanceCompliance.verifications.interviewDetailsLocation)
        );
      }

      if (workflow?.isHomeLoan() && homeLoanSecurityDetails?.securitySplits?.length) {
        for (let s = 0; s < homeLoanSecurityDetails.securitySplits.length; s += 1) {
          const securitySplit = homeLoanSecurityDetails.securitySplits[s];
          if (_.get(securitySplit, 'address')) {
            const address = _.get(securitySplit, 'address');
            if (Address.hasAddress(address) && !isAddressExisted({ addresses, address })) {
              addresses.push(Address.build(address));
            }
          }
        }
      }
      if (workflow?.isHomeLoan() && homeLoanSecurityDetails?.contactsForValuation?.length) {
        for (let s = 0; s < homeLoanSecurityDetails.contactsForValuation.length; s += 1) {
          const contactForValuation = homeLoanSecurityDetails.contactsForValuation[s];
          if (_.get(contactForValuation, 'address')) {
            const address = _.get(contactForValuation, 'address');
            if (Address.hasAddress(address) && !isAddressExisted({ addresses, address })) {
              addresses.push(Address.build(address));
            }
          }
        }
      }
      if (workflow?.isHomeLoan() && homeLoanCompliance?.verifications?.applicants?.length) {
        for (let a = 0; a < homeLoanCompliance.verifications.applicants.length; a += 1) {
          const applicant = homeLoanCompliance.verifications.applicants[a];
          if (_.get(applicant, 'address')) {
            const address = _.get(applicant, 'address');
            if (Address.hasAddress(address) && !isAddressExisted({ addresses, address })) {
              addresses.push(Address.build(address));
            }
          }
        }
      }
      if (
        workflow?.isHomeLoan() &&
        Address.hasAddress(homeLoanCompliance?.verifications?.interviewDetailsLocation) &&
        !isAddressExisted({
          addresses,
          address: homeLoanCompliance?.verifications?.interviewDetailsLocation
        })
      ) {
        addresses.push(
          Address.build(homeLoanCompliance.verifications.interviewDetailsLocation)
        );
      }
    }

    if (
      workflow?.isRiskInsurance() &&
      riskInsuranceCompliance?.verifications?.identities?.length
    ) {
      for (let a = 0; a < riskInsuranceCompliance.verifications.identities.length; a += 1) {
        const applicant = riskInsuranceCompliance.verifications.identities[a];
        if (_.get(applicant, 'address')) {
          const address = _.get(applicant, 'address');
          if (Address.hasAddress(address) && !isAddressExisted({ addresses, address })) {
            addresses.push(Address.build(address));
          }
        }
      }
    }
    if (
      workflow?.isRiskInsurance() &&
      Address.hasAddress(riskInsuranceCompliance?.verifications?.interviewDetails?.address) &&
      !isAddressExisted({
        addresses,
        address: riskInsuranceCompliance?.verifications?.interviewDetails?.address
      })
    ) {
      addresses.push(
        Address.build(riskInsuranceCompliance.verifications.interviewDetails.address)
      );
    }
    return _.uniqBy(addresses, 'id');
  };

  /**
   * Has asset with asset type owner occupied or investment.
   *
   * @param {Array} assets
   *
   * @returns {Boolean}
   */
  const hasAssetWithAssetTypeOwnerOccupiedOrInvestment = ({ assets, type }) => {
    let filteredAssets = [];
    if (!type) {
      filteredAssets = _.filter(
        assets,
        (a) => isAssetTypeOwnerOccupiedOrInvestment(a.assetType) && a.getName() !== ''
      );
    }
    if (type === 'OO') {
      filteredAssets = _.filter(
        assets,
        (a) => isAssetTypeOwnerOccupied(a.assetType) && a.getName() !== ''
      );
    }
    if (type === 'INV') {
      filteredAssets = _.filter(
        assets,
        (a) => isAssetTypeInvestment(a.assetType) && a.getName() !== ''
      );
    }
    return filteredAssets.length > 0;
  };

  /**
   * Has asset with asset type vehicle.
   *
   * @param {Array} assets
   *
   * @returns {Boolean}
   */
  const hasAssetWithAssetTypeVehicle = (assets) => {
    const filteredAssets = _.filter(
      assets,
      (a) => isAssetTypeVehicle(a.assetType) && a.getName() !== ''
    );
    return filteredAssets.length > 0;
  };

  /**
   * Has asset commitment schedule.
   *
   * @param {Array} assetCommitmentSchedules
   *
   * @returns {Boolean}
   */
  const hasAssetCommitmentSchedule = (assetCommitmentSchedules) => {
    const collections = [];
    const len = assetCommitmentSchedules?.length;
    for (let i = 0; i < len; i += 1) {
      const assetCommitmentSchedule = assetCommitmentSchedules[i];
      collections.push(assetCommitmentSchedule?.assetCommitments?.length > 0);
    }
    const hasAsset = _.some(collections, (c) => c === true);
    return hasAsset;
  };

  /**
   * Get last index.
   *
   * @param {Array} dataToSort
   * @param {String} idType
   *
   * @returns {Boolean}
   */
  const getLastIndex = (dataToSort, idType) => {
    let data = dataToSort;
    data = _.sortBy(data, (o) => {
      if (o instanceof Asset) {
        return o?.assetType?.weight;
      }
      if (o instanceof Liability) {
        return o?.liabilityType?.weight;
      }
      if (o instanceof Insurance) {
        return o?.insuranceType?.weight;
      }
      if (o instanceof OtherAdviser) {
        return o?.otherAdviserType?.weight;
      }
      if (o instanceof RiskInsuranceProduct) {
        return o?.productType?.weight;
      }
      return null;
    });
    return _.findLastIndex(data, (i) => {
      if (i instanceof Asset) {
        return i?.assetType?.id === idType;
      }
      if (i instanceof Liability) {
        return i?.liabilityType?.id === idType;
      }
      if (i instanceof Insurance) {
        return i?.insuranceType?.id === idType;
      }
      if (i instanceof OtherAdviser) {
        return i?.otherAdviserType?.id === idType;
      }
      if (i instanceof RiskInsuranceProduct) {
        return i?.productType?.name === idType?.name;
      }
      return null;
    });
  };

  /**
   * Is lender selected.
   *
   * @param {Array} lenders
   * @param {String} lender
   *
   * @returns {Boolean}
   */
  const isLenderSelected = ({ lenders, lender }) => {
    const currentLender = _.find(
      lenders || [],
      (l) => l?.abbr?.toLowerCase() === lender?.toLowerCase()
    );
    return currentLender !== undefined;
  };

  /**
   * Purchase owner occupied status.
   *
   * @param {Array} assets
   * @param {Array} homeLoanSecurities
   * @param {Object} contact
   *
   * @returns {Boolean}
   */
  const purchaseOwnerOccupiedStatus = ({ assets, homeLoanSecurities, contact }) => {
    const collection = [];
    if (homeLoanSecurities?.length) {
      const pOO = _.some(
        homeLoanSecurities,
        (s) => s?.ownershipType?.name === OwnershipType.owner_occupied.name
      );
      collection.push(pOO);
    }
    if (assets?.length) {
      const invToBeOO = _.filter(
        assets,
        (a) => isAssetTypeInvestment(a?.assetType) && a?.willItBecomeOwnerOccupied === true
      );
      const toBeInv = _.forEach(invToBeOO, (i) =>
        _.some(i?.ownership, (o) => o?.idContact === contact?.id && o?.percent > 0)
      );
      collection.push(toBeInv);
    }
    return _.some(collection, (i) => i === true);
  };

  /**
   * Get set purchase owner occupied status.
   *
   * @param {Array} assets
   * @param {Array} homeLoanSecurities
   * @param {Object} contact
   *
   * @returns {Boolean}
   */
  const getPostSettlementStatus = ({ assets, homeLoanSecurities, contact }) => {
    if (purchaseOwnerOccupiedStatus({ assets, homeLoanSecurities, contact })) {
      return false;
    }
    return (
      contact?.person?.contact?.settlementHousing?.name ===
        ContactResidentialStatusOption.boarding.name ||
      contact?.person?.contact?.settlementHousing?.name ===
        ContactResidentialStatusOption.with_parents.name ||
      false
    );
  };

  /**
   * Set chart of compare loan products.
   *
   * @param {Array} compareProducts
   *
   * @returns {Object}
   */
  const setChartOfCompareLoanProducts = ({ compareProducts }) => {
    const currentCurrency = $rootScope.CurrentCurrency();
    const chart = {
      values: [],
      isStack: true,
      height: '100%',
      width: '100%',
      legends: [
        {
          name: `Loan amount`,
          value: []
        },
        {
          name: `Interest`,
          value: []
        },
        {
          name: `Fees`,
          value: []
        }
      ],
      colors: ['#03A9F4', '#F44336', '#FF9800'],
      symbol: currentCurrency?.symbol
    };
    const len = compareProducts?.length || 0;
    for (let i = 0; i < len; i += 1) {
      const compareProduct = compareProducts[i];
      const value = {
        name: `Option #${i + 1}${compareProduct?.isPreferred ? ' preferred' : ''}`,
        value: [
          round({ value: compareProduct?.costs?.principal || 0 }),
          round({ value: compareProduct?.costs?.interest || 0 }),
          round({ value: compareProduct?.costs?.fees || 0 })
        ]
      };

      if (compareProduct?.isPreferred) {
        chart.values.push(value);
        chart.legends[0].value.unshift(
          `Option #${_.add(i, 1)} - ${round({
            value: compareProduct?.costs?.principal || 0
          }).formatWithCurrency(currentCurrency)}`
        );
        chart.legends[1].value.unshift(
          `Option #${_.add(i, 1)}: ${round({
            value: compareProduct?.costs?.interest || 0
          }).formatWithCurrency(currentCurrency)}`
        );
        chart.legends[2].value.unshift(
          `Option #${_.add(i, 1)} - ${round({
            value: compareProduct?.costs?.fees || 0
          }).formatWithCurrency(currentCurrency)}`
        );
      } else {
        chart.values.unshift(value);
        chart.legends[0].value.push(
          `Option #${_.add(i, 1)} - ${round({
            value: compareProduct?.costs?.principal || 0
          }).formatWithCurrency(currentCurrency)}`
        );
        chart.legends[1].value.push(
          `Option #${_.add(i, 1)} - ${round({
            value: compareProduct?.costs?.interest || 0
          }).formatWithCurrency(currentCurrency)}`
        );
        chart.legends[2].value.push(
          `Option #${_.add(i, 1)} - ${round({
            value: compareProduct?.costs?.fees || 0
          }).formatWithCurrency(currentCurrency)}`
        );
      }
    }
    chart.isReady = true;
    return chart;
  };

  /**
   * Get preferred product.
   *
   * @param {Array} products
   *
   * @returns {Object}
   */
  const getPreferredProduct = ({ products }) => _.find(products || [], (p) => p?.isPreferred);

  /**
   * Get lender supporting documents.
   *
   * @param {Array} lenderDocuments
   * @param {Array} products
   *
   * @returns {Object}
   */
  const getLenderSupportingDocuments = ({ lenderDocuments, products }) => {
    const preferredProduct = getPreferredProduct({ products });
    return _.filter(
      lenderDocuments || [],
      (d) =>
        d?.lender[0]?.id === preferredProduct?.idLender &&
        d?.type?.indexOf('SUPPORTING_DOCUMENT') > -1
    );
  };

  /**
   * Is Loanworks gateway selected.
   *
   * @param {String} idLender
   *
   * @returns {Boolean}
   */
  const isLoanworksGatewaySelected = ({ idLender }) =>
    (($rootScope.isDev() || $rootScope.isApp()) &&
      (idLender === 'a422e5e5a235a545d128c0f698e9cebe40b78cc484bf254046cd3421262e9de9' ||
        idLender === '48e3d546cc6279dadae4ab3dace3ecd661cff2e1e6c2d79eddea12ff017503a6')) ||
    ($rootScope.isYnt() &&
      (idLender === 'a422e5e5a235a545d128c0f698e9cebe40b78cc484bf254046cd3421262e9de9' ||
        idLender === '48e3d546cc6279dadae4ab3dace3ecd661cff2e1e6c2d79eddea12ff017503a6')) ||
    ($rootScope.isVow() &&
      idLender === '48e3d546cc6279dadae4ab3dace3ecd661cff2e1e6c2d79eddea12ff017503a6') ||
    ($rootScope.isPtf() &&
      idLender === '48e3d546cc6279dadae4ab3dace3ecd661cff2e1e6c2d79eddea12ff017503a6') ||
    ($rootScope.isChf() &&
      idLender === '48e3d546cc6279dadae4ab3dace3ecd661cff2e1e6c2d79eddea12ff017503a6');

  /**
   * Get status of "required" compliance fields.
   *
   * @param {Array} products
   * @param {Array} validationErrors
   * @param {Object} compliance
   * @param {Object} objectives
   * @param {Object} commission
   * @param {String} idPreferredLender
   * @param {Boolean} isCommercial
   * @param {Boolean} hasAssetFinanceTools
   * @param {Boolean} hasHomeLoanTools
   * @param {Boolean} isLodgement
   *
   * @returns {Boolean}
   */
  const getStatusOfRequiredComplianceFields = ({
    products,
    validationErrors,
    compliance,
    objectives,
    commission,
    idPreferredLender,
    isCommercial,
    hasAssetFinanceTools,
    hasHomeLoanTools,
    isDocument
  }) => {
    if (hasAssetFinanceTools && !isCommercial) {
      if (
        compliance &&
        compliance?.comments &&
        compliance?.interestDisclosure &&
        compliance?.productSuitability
      ) {
        const collection = [];
        if (getOrganizationCountry().cca2 !== 'NZ') {
          collection.push(!validationErrors?.length);
        }
        collection.push(compliance?.comments?.livingExpenses !== '');
        collection.push(compliance?.comments?.analysisAssessmentAndApplicantEducation !== '');
        collection.push(compliance?.comments?.abilityToServiceFinancialAnalysis !== '');
        collection.push(compliance?.comments?.optionsPresentedAndRecommendation !== '');
        collection.push(compliance?.comments?.general !== '');
        collection.push(
          compliance?.basisForRecommendationsMade?.paymentFrequency &&
            (compliance?.basisForRecommendationsMade?.financeTerm?.longer ||
              compliance?.basisForRecommendationsMade?.financeTerm?.earlier)
        );
        if (
          !_.isEmpty(compliance?.basisForRecommendationsMadeAndCustomerDisclosures) &&
          getOrganizationCountry().cca2 !== 'NZ'
        ) {
          collection.push(
            compliance?.basisForRecommendationsMadeAndCustomerDisclosures
              ?.designDistributionObligations?.name === YesNoOption.yes.name ||
              compliance?.basisForRecommendationsMadeAndCustomerDisclosures
                ?.designDistributionObligations?.name === YesNoOption.no.name
          );
          if (
            compliance?.basisForRecommendationsMadeAndCustomerDisclosures
              ?.designDistributionObligations?.name === YesNoOption.no.name
          ) {
            collection.push(
              compliance?.basisForRecommendationsMadeAndCustomerDisclosures
                ?.designDistributionObligationsComments !== ''
            );
          }
        }
        if (
          compliance?.interestDisclosure?.isConflict === true ||
          compliance?.interestDisclosure?.isConflict?.name === YesNoOption.yes.name
        ) {
          if (compliance?.interestDisclosure?.isConflict === true) {
            collection.push(compliance?.interestDisclosure?.comments !== '');
          }
          if (compliance?.interestDisclosure?.isConflict?.name === YesNoOption.yes.name) {
            collection.push(
              (compliance?.interestDisclosure?.lenderConflictOfInterest &&
                compliance?.interestDisclosure?.lenderConflictOfInterestComments !== '') ||
                (compliance?.interestDisclosure?.referralConflictOfInterest &&
                  compliance?.interestDisclosure?.referralConflictOfInterestComments !== '') ||
                (compliance?.interestDisclosure?.otherConflictOfInterest &&
                  compliance?.interestDisclosure?.otherConflictOfInterestComments !== '')
            );
          }
        }
        if (compliance.productSuitability.isUnsuitable === true) {
          collection.push(
            compliance.productSuitability.isProductInconsistent ||
              compliance.productSuitability.isApplicantComply
          );
        }
        if (isDocument) {
          collection.push(
            commission?.idCommission !== '' && commission?.commissionSplits?.length > 0
          );
        }
        return _.every(collection, (i) => i === true);
      }
      return true;
    }
    if (hasHomeLoanTools) {
      const isLoanworks = isLoanworksGatewaySelected({
        idLender: idPreferredLender
      });
      if (
        compliance &&
        compliance?.comments &&
        compliance?.interestDisclosure &&
        compliance?.productSuitability
      ) {
        const collection = [];
        const status =
          isHomeLoanPreferredProductFixedInterestOnlyLineOfCreditOrInterestInAdvance(products);

        if (getOrganizationCountry().cca2 === 'NZ') {
          collection.push(compliance.comments.security !== '');
        } else {
          collection.push(!validationErrors?.length);
          collection.push(compliance.comments.analysisAssessmentAndApplicantEducation !== '');
          collection.push(compliance.comments.optionsPresentedAndRecommendation !== '');
        }

        collection.push(compliance.comments.borrowingPower !== '');
        collection.push(compliance.comments.depositOrEquity !== '');
        collection.push(compliance.comments.creditHistory !== '');
        collection.push(compliance.comments.applicationSubmission !== '');
        collection.push(compliance.comments.livingExpenses !== '');
        if (status?.isInterestOnly || status?.isFixed) {
          if (status?.isInterestOnly) {
            collection.push(compliance.comments.interestOnlyProduct !== '');
          }
          if (status?.isFixed) {
            collection.push(compliance.comments.fixedProduct !== '');
          }
        }
        if (
          compliance?.interestDisclosure?.isConflict === true ||
          compliance?.interestDisclosure?.isConflict?.name === YesNoOption.yes.name
        ) {
          if (compliance?.interestDisclosure?.isConflict === true) {
            collection.push(compliance?.interestDisclosure?.comments !== '');
          }
          if (compliance?.interestDisclosure?.isConflict?.name === YesNoOption.yes.name) {
            collection.push(
              (compliance?.interestDisclosure?.lenderConflictOfInterest &&
                compliance?.interestDisclosure?.lenderConflictOfInterestComments !== '') ||
                (compliance?.interestDisclosure?.referralConflictOfInterest &&
                  compliance?.interestDisclosure?.referralConflictOfInterestComments !== '') ||
                (compliance?.interestDisclosure?.otherConflictOfInterest &&
                  compliance?.interestDisclosure?.otherConflictOfInterestComments !== '')
            );
          }
        }
        if (compliance.productSuitability.isUnsuitable === true) {
          collection.push(
            compliance.productSuitability.isProductInconsistent ||
              compliance.productSuitability.isApplicantComply
          );
        }
        if (objectives?.requirementsAndObjectives?.purposeDetails?.refinance) {
          collection.push(
            objectives?.requirementsAndObjectives?.applicantsUnderstandPurposes?.name !==
              YesNoOption.none.name &&
              objectives?.requirementsAndObjectives
                ?.applicantsUnderstandThatMayEndUpPayMoreInterest?.name !==
                YesNoOption.none.name
          );
        }
        if (objectives?.rateType?.fixed?.type?.name === ImportantOption.important.name) {
          collection.push(
            !isLoanworks
              ? objectives?.rateType?.fixed?.isConfirmed?.name !== YesNoOption.none.name
              : objectives?.rateType?.fixed?.isConfirmed?.name === YesNoOption.yes.name
          );
        }
        if (objectives?.rateType?.variable?.type?.name === ImportantOption.important.name) {
          collection.push(
            !isLoanworks
              ? objectives?.rateType?.variable?.isConfirmed?.name !== YesNoOption.none.name
              : objectives?.rateType?.variable?.isConfirmed?.name === YesNoOption.yes.name
          );
        }
        if (
          objectives?.rateType?.fixedAndVariable?.type?.name === ImportantOption.important.name
        ) {
          collection.push(
            !isLoanworks
              ? objectives?.rateType?.fixedAndVariable?.isConfirmed?.name !==
                  YesNoOption.none.name
              : objectives?.rateType?.fixedAndVariable?.isConfirmed?.name ===
                  YesNoOption.yes.name
          );
        }
        if (
          objectives?.repaymentType?.principalAndInterest?.type?.name ===
          ImportantOption.important.name
        ) {
          collection.push(
            !isLoanworks
              ? objectives?.repaymentType?.principalAndInterest?.isConfirmed?.name !==
                  YesNoOption.none.name
              : objectives?.repaymentType?.principalAndInterest?.isConfirmed?.name ===
                  YesNoOption.yes.name
          );
        }
        if (
          objectives?.repaymentType?.interestOnly?.type?.name === ImportantOption.important.name
        ) {
          collection.push(
            !isLoanworks
              ? objectives?.repaymentType?.interestOnly?.isConfirmed?.name !==
                  YesNoOption.none.name
              : objectives?.repaymentType?.interestOnly?.isConfirmed?.name ===
                  YesNoOption.yes.name
          );
        }
        if (
          objectives?.repaymentType?.interestInAdvance?.type?.name ===
          ImportantOption.important.name
        ) {
          collection.push(
            !isLoanworks
              ? objectives?.repaymentType?.interestInAdvance?.isConfirmed?.name !==
                  YesNoOption.none.name
              : objectives?.repaymentType?.interestInAdvance?.isConfirmed?.name ===
                  YesNoOption.yes.name
          );
        }
        if (
          objectives?.repaymentType?.lineOfCredit?.type?.name === ImportantOption.important.name
        ) {
          collection.push(
            !isLoanworks
              ? objectives?.repaymentType?.lineOfCredit?.isConfirmed?.name !==
                  YesNoOption.none.name
              : objectives?.repaymentType?.lineOfCredit?.isConfirmed?.name ===
                  YesNoOption.yes.name
          );
        }
        if (
          objectives?.productType?.offsetAccount?.type?.name === ImportantOption.important.name
        ) {
          collection.push(
            !isLoanworks
              ? objectives?.productType?.offsetAccount?.isConfirmed?.name !==
                  YesNoOption.none.name
              : objectives?.productType?.offsetAccount?.isConfirmed?.name ===
                  YesNoOption.yes.name
          );
        }
        if (objectives?.productType?.redraw?.type?.name === ImportantOption.important.name) {
          collection.push(
            !isLoanworks
              ? objectives?.productType?.redraw?.isConfirmed?.name !== YesNoOption.none.name
              : objectives?.productType?.redraw?.isConfirmed?.name === YesNoOption.yes.name
          );
        }
        if (
          !_.isEmpty(compliance?.brokerDisclosure) &&
          getOrganizationCountry().cca2 !== 'NZ'
        ) {
          collection.push(
            compliance?.brokerDisclosure?.designDistributionObligations?.name ===
              YesNoOption.yes.name ||
              compliance?.brokerDisclosure?.designDistributionObligations?.name ===
                YesNoOption.no.name
          );
          if (
            compliance?.brokerDisclosure?.designDistributionObligations?.name ===
            YesNoOption.no.name
          ) {
            collection.push(
              compliance?.brokerDisclosure?.designDistributionObligationsComments !== ''
            );
          }
        }
        if (
          !_.isEmpty(compliance?.brokerDisclosure) &&
          getOrganizationCountry().cca2 === 'NZ'
        ) {
          collection.push(
            compliance?.brokerDisclosure?.capitalisationOfFees?.name === YesNoOption.yes.name ||
              compliance?.brokerDisclosure?.capitalisationOfFees?.name === YesNoOption.no.name
          );
          if (
            compliance?.brokerDisclosure?.capitalisationOfFees?.name === YesNoOption.no.name
          ) {
            collection.push(compliance?.brokerDisclosure?.capitalisationOfFeesComments !== '');
          }
        }
        if (!_.isEmpty(objectives?.conflictsBetweenLoanFeatures)) {
          collection.push(
            objectives?.conflictsBetweenLoanFeatures?.anyConflictsMarkedAsImportant?.name ===
              YesNoOption.yes.name ||
              objectives?.conflictsBetweenLoanFeatures?.anyConflictsMarkedAsImportant?.name ===
                YesNoOption.no.name
          );
          if (
            objectives?.conflictsBetweenLoanFeatures?.anyConflictsMarkedAsImportant?.name ===
            YesNoOption.yes.name
          ) {
            collection.push(objectives?.conflictsBetweenLoanFeatures?.conflictsComments !== '');
          }
        }
        if (isDocument) {
          collection.push(
            commission?.idCommission !== '' && commission?.commissionSplits?.length > 0
          );
        }
        return _.every(collection, (i) => i === true);
      }
      return true;
    }
    return true;
  };

  /**
   * Show option for creating PDF application.
   *
   * @param {Object} workflow
   * @param {Boolean} hasAssetFinanceTools
   * @param {Boolean} hasHomeLoanTools
   *
   * @returns {Boolean}
   */
  const showOptionForCreatingPdfApplication = ({
    workflow,
    hasAssetFinanceTools,
    hasHomeLoanTools
  }) => {
    if (getOrganizationCountry().cca2 === 'AU') {
      return (
        (workflow?.isAssetFinance() && hasAssetFinanceTools) ||
        (workflow?.isHomeLoan() && hasHomeLoanTools)
      );
    }
    if (getOrganizationCountry().cca2 === 'NZ') {
      return workflow?.isHomeLoan() && hasHomeLoanTools;
    }
    return false;
  };

  /**
   * Get alert model.
   *
   * @returns {Object}
   */
  const getAlertDialogModel = () => ({
    event: null,
    alerts: [],
    icon: '',
    title: ''
  });

  /**
   * Get alert model.
   *
   * @returns {Object}
   */
  const getAlertModel = () => ({
    messages: [],
    icon: '',
    type: ''
  });

  /**
   * Show alert for creating PDF application.
   *
   * @param {Array} products
   * @param {Object} compliance
   * @param {Object} objectives
   * @param {Object} commission
   * @param {Object} workflow
   * @param {Boolean} hasAssetFinanceTools
   * @param {Boolean} hasHomeLoanTools
   * @param {Boolean} isDocument
   *
   * @returns {Boolean}
   */
  const showAlertForCreatingPdfApplication = ({
    products,
    compliance,
    objectives,
    commission,
    workflow,
    hasAssetFinanceTools,
    hasHomeLoanTools,
    isDocument
  }) => {
    const alertDialog = getAlertDialogModel();
    alertDialog.icon = 'offline_bolt';
    const preferredProduct = getPreferredProduct({
      products
    });
    if (workflow?.isAssetFinance() && hasAssetFinanceTools) {
      if (getOrganizationCountry().cca2 === 'AU') {
        if (!preferredProduct) {
          const alert = getAlertModel();
          alert.messages.push('Currently, there are no selected recommended products.');
          alert.icon = 'offline_bolt';
          alert.type = 'error';
          alertDialog.alerts.push(alert);
        }
      }
    }
    if (workflow?.isHomeLoan() && hasHomeLoanTools) {
      if (getOrganizationCountry().cca2 === 'NZ') {
        if (!preferredProduct) {
          const alert = getAlertModel();
          alert.messages.push('Currently, there are no selected recommended products.');
          alert.icon = 'offline_bolt';
          alert.type = 'error';
          alertDialog.alerts.push(alert);
        }
      }
      if (getOrganizationCountry().cca2 === 'AU') {
        const statusOfRequiredComplianceFields = getStatusOfRequiredComplianceFields({
          products: products || [],
          compliance: compliance || {},
          objectives: objectives || {},
          commission: commission || {},
          hasHomeLoanTools,
          isDocument
        });
        if (!preferredProduct) {
          const alert = getAlertModel();
          alert.messages.push('Currently, there are no selected recommended products.');
          alert.icon = 'offline_bolt';
          alert.type = 'error';
          alertDialog.alerts.push(alert);
        }
        if (!statusOfRequiredComplianceFields) {
          const alert = getAlertModel();
          alert.messages.push(
            'Required compliance and/or commission fields are not filled in on the Compliance and documents section.'
          );
          alert.icon = 'offline_bolt';
          alert.type = 'error';
          alertDialog.alerts.push(alert);
        }
      }
    }
    return alertDialog;
  };

  /**
   * Has validation error.
   *
   * @param {Array} errors
   * @param {String} path
   *
   * @returns {Boolean}
   */
  const hasValidationError = ({ errors, path }) => {
    const error = _.find(
      errors,
      (e) => e?.path?.join('.')?.toLowerCase() === path?.toLowerCase()
    );
    return error !== undefined;
  };

  /**
   * Process open banking data.
   *
   * @param {Object} data
   * @param {Object} ticket
   * @param {Array} contacts
   *
   * @returns {Array}
   */
  const processOpenBankingData = ({ data, ticket, contacts }) => {
    const ticketToUpdate = ticket;
    if (data?.type === 'income' && data?.selectedData?.length) {
      const iLen = data?.selectedData?.length || 0;
      for (let i = 0; i < iLen; i += 1) {
        const item = data.selectedData[i];
        ticketToUpdate.income.push(Income.blank(item.incomeType));
        const lastIdx = ticketToUpdate?.income?.length - 1;
        ticketToUpdate.assets[lastIdx].grossValue = item?.grossValue || 0;
        ticketToUpdate.assets[lastIdx].frequency = item?.frequency || PaymentFrequency.none;
        ticketToUpdate.assets[lastIdx].payg.grossSalary = item?.payg?.grossSalary || 0;
        ticketToUpdate.assets[lastIdx].payg.grossSalaryFrequency =
          item?.payg?.grossSalaryFrequency || PaymentFrequency.none;
        ticketToUpdate.assets[lastIdx].idContact = item?.idContact || '';
      }
      return ticketToUpdate.income;
    }
    if (data?.type === 'expenses' && data?.selectedData?.length) {
      //
    }
    if (data?.type === 'assets' && data?.selectedData?.length) {
      const aLen = data?.selectedData?.length || 0;
      for (let a = 0; a < aLen; a += 1) {
        const item = data.selectedData[a];
        const ownership = setOwnershipOfAssetOrLiability(contacts, true);
        ticketToUpdate.assets.push(Asset.blank(item?.assetType, ownership));
        const lastIdx = ticketToUpdate?.assets?.length - 1;
        ticketToUpdate.assets[lastIdx].name = item?.name;
        ticketToUpdate.assets[lastIdx].bankOtherName = item?.bankOtherName;
        ticketToUpdate.assets[lastIdx].bankAccountType = BankAccountType.of(
          item?.bankAccountType?.name
        );
        ticketToUpdate.assets[lastIdx].value = item?.value;
        ticketToUpdate.assets[lastIdx].ownership = item?.ownership;
      }
      return ticketToUpdate.assets;
    }
    if (data?.type === 'liabilities' && data?.selectedData?.length) {
      const lLen = data?.selectedData?.length || 0;
      for (let l = 0; l < lLen; l += 1) {
        const item = data.selectedData[l];
        const ownership = setOwnershipOfAssetOrLiability(contacts, true);
        ticketToUpdate.liabilities.push(Liability.blank(item?.liabilityType, ownership));
        const lastIdx = ticketToUpdate?.liabilities?.length - 1;
        ticketToUpdate.liabilities[lastIdx].name = item?.name;
        ticketToUpdate.liabilities[lastIdx].lenderOtherName = item?.lenderOtherName;
        ticketToUpdate.liabilities[lastIdx].limit = item?.limit;
        ticketToUpdate.liabilities[lastIdx].balance = item?.balance;
        ticketToUpdate.liabilities[lastIdx].repayment = item?.repayment;
        ticketToUpdate.liabilities[lastIdx].ownership = item?.ownership;
      }
      return ticketToUpdate.liabilities;
    }
    return null;
  };

  /**
   * Errors alert.
   *
   * @param {Array} errors
   * @param {String} errorMessage
   * @param {String} title
   *
   * @returns {Object}
   */
  const errorsAlert = ({ errors, errorMessage, title }) => {
    const alertDialog = getAlertDialogModel();
    alertDialog.title = `Errors: ${title}`;
    alertDialog.icon = 'offline_bolt';

    if (errors?.length) {
      const len = errors?.length || 0;
      for (let e = 0; e < len; e += 1) {
        const error = errors[e];
        const alert = getAlertModel();
        alert.messages.push(
          error?.label
            ? `${error?.label?.sanitize()} -> ${error?.message || ''}`
            : error?.message || error || ''
        );
        alert.icon = 'offline_bolt';
        alert.type = 'error';
        alertDialog.alerts.push(alert);
      }
    } else {
      const alert = getAlertModel();
      alert.messages.push(errorMessage);
      alert.icon = 'offline_bolt';
      alert.type = 'error';
      alertDialog.alerts.push(alert);
    }
    return alertDialog;
  };

  return {
    setOtherDateFormat,
    getOrganizationCountry,
    getOrganizationVat,
    getGstThreshold,
    round,
    compareTwoArraysOfObjects,
    sortArrayOfObjectsBasedOnAnotherArray,
    getTicketContactModel,
    getAllClientPortalSections,
    getAvailableClientPortalSections,
    getAvailableRepaymentIndexes,
    getPrimaryBuffer,
    getSecondaryBuffer,
    isUsingCompareWithHem,
    getSetAvailableComplianceTypes,
    getAvailableAddressStates,
    getAvailableAddressStreetTypes,
    getAvailableContactEmploymentBasis,
    getAvailableTermInYears,
    getAvailableTermInYearsShownInMonths,
    getAvailableTermInMonths,
    getAvailableTermInMonthsShownInYears,
    getTitleOfSelectedTermInMonths,
    getAvailableYears,
    getAvailableIIncomeLastTaxYears,
    getAvailableIIncomePreviousTaxYears,
    getAvailableInterestOnlyReason,
    getAvailableRatings,
    getAvailableLevelOptions,
    getAvailablePaymentFrequencies,
    getNetIncome,
    getAvailableObjectivePaymentPreferenceOptions,
    getAvailableMonthlyRepayments,
    getAvailableInterestRates,
    getAvailableCreditRecordOptions,
    getSimpleSelectOptions,
    getAvailableInterviewMethods,
    getAvailableComplianceVerificationsInterviewDetailsMethods,
    getAvailableIncomeVerificationCompleted,
    getAvailableRefinanceTypes,
    getAvailableOtherContactTypesForValuation,
    getAvailableMortgagePriorities,
    getAvailableNatureOfServiceSought,
    getAvailableLocationOfAssetsOrFunds,
    getAvailableLeadSourcingMethod,
    getAvailablePropertyStatus,
    getAvailableConstructionTypes,
    getAvailableConstructionPropertyTypes,
    getAvailableEmploymentVerificationMethods,
    getAvailableComplianceVerificationsEmploymentOptions,
    getAvailableYesNoOptions,
    getAvailableYesNoSelectOptions,
    getAvailableObjectivePreferredDurationOptions,
    getAvailableObjectivePaymentPlanOptions,
    getAvailableImportantOptions,
    getAvailableHowImportantOptions,
    getAvailableFrequencyOptions,
    getProductFeeTypeRelations,
    getAvailableProductFeeTypes,
    getAvailableAssetFinanceAssetAges,
    getAvailableConveyancingFeeTypes,
    getAvailableRealEstateFeeTypes,
    getAvailablePdfTypes,
    getAvailableComplianceTypes,
    getAvailableClientTypesForCreateDocumentsOption,
    getBidFlows,
    getDate,
    isNumber,
    findIndexById,
    findIndexByIdContact,
    findByIdContact,
    rearrangeArrayByIndex,
    getIdLabelsOfClient,
    sortContactLabels,
    getAgeOfDependent,
    isClientNonBorrowingSpouse,
    isClientSecurityGuarantor,
    isClientServicingGuarantor,
    setOwnershipOfAssetOrLiability,
    getOwnershipPercentageOfAssetOrLiabilityPerContact,
    getOwnerships,
    editOwnershipOfAssetOrLiability,
    getOwnershipForNewProperties,
    isAssetTypeOwnerOccupied,
    isAssetTypeInvestment,
    isAssetTypeOwnerOccupiedOrInvestment,
    isAssetTypeVehicle,
    isAssetTypeBankAccount,
    isAssetTypeHomeContents,
    isAssetTypeKiwiSaver,
    isAssetTypeSuperfund,
    isAssetTypeShares,
    isAssetTypeOther,
    isAssetTypeBalanceSheet,
    getOwnerOccupiedAssets,
    getInvestmentAssets,
    getAssetCommitmentScheduleTotal,
    isLiabilityTypeMortgage,
    isLiabilityTypeCreditCard,
    isLiabilityTypeOverdraft,
    isLiabilityTypeVehicle,
    isLiabilityTypePersonal,
    isLiabilityTypeSmsf,
    isLiabilityTypeEducationDebt,
    isLiabilityTypeOther,
    isLiabilityTypeBalanceSheet,
    isLiabilityTypeLoan,
    isLiabilityTypeMortgageOrVehicleOrSmsf,
    isLiabilityTypeMortgageOrSmsfLoan,
    hasEducationDebtPerContact,
    isLiabilityLinkedToAssetMarkedAsToBeSold,
    getTotalExistingLiabilitiesRepayments,
    getTotalProposedExistingRepaymentsAtBufferRate,
    getTotalProposedLiabilitiesRepayments,
    getInvestmentHomeLoanSecurities,
    isSelfEmployedInACompany,
    getPurchaseOwnerOccupiedStatus,
    getStateAndPostCodeByContact,
    findIndexByContactId,
    sortArrayBasedOnAnotherArray,
    getFrequencyValue,
    isStateValid,
    isValidAbn,
    removeItemFromArray,
    deleteItem,
    getContact,
    getContactsByContentType,
    getContactTypeInDeal,
    getPreferredContactMethod,
    getLinkedAsset,
    getLinkedInsurance,
    getLenderByContentType,
    getHomeLoanSecurityContactForValuationType,
    getLinkedHomeLoanSecurity,
    isPropertyUsedAsSecurity,
    isLiabilityUsedInHomeLoan,
    getAssetFinanceProductType,
    getAssetFinanceAssetType,
    getAssetFinanceAssetAge,
    getAssetFinanceVendorType,
    getAssetFinancePaymentFrequency,
    getAvailableObjectivePayBallonOptions,
    getAllAddresses,
    hasAssetWithAssetTypeOwnerOccupiedOrInvestment,
    hasAssetWithAssetTypeVehicle,
    hasAssetCommitmentSchedule,
    getLastIndex,
    isLenderSelected,
    getPostSettlementStatus,
    setChartOfCompareLoanProducts,
    getPreferredProduct,
    getLenderSupportingDocuments,
    isLoanworksGatewaySelected,
    getStatusOfRequiredComplianceFields,
    showOptionForCreatingPdfApplication,
    getAlertDialogModel,
    getAlertModel,
    showAlertForCreatingPdfApplication,
    hasValidationError,
    processOpenBankingData,
    errorsAlert
  };
};
